import React, { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import {
  AccessTimeOutlined,
  ArrowForwardOutlined,
  ArrowOutwardOutlined,
  AssessmentOutlined,
  BoltOutlined,
  BusinessOutlined,
  CalendarTodayOutlined,
  CategoryOutlined,
  CheckCircleOutlineOutlined,
  CompareArrowsOutlined,
  ExpandMoreOutlined,
  EventNoteOutlined,
  FactCheckOutlined,
  GppGoodOutlined,
  Inventory2Outlined,
  ImageOutlined,
  InfoOutlined,
  LockOutlined,
  LocationOnOutlined,
  PlaceOutlined,
  PictureAsPdfOutlined,
  ReportProblemOutlined,
  ShieldOutlined,
  TableChartOutlined,
  TaskAltOutlined,
  TrackChangesOutlined,
  TrendingUpOutlined,
  WarningAmberOutlined,
  VideocamOutlined,
  VisibilityOutlined,
  DescriptionOutlined,
  DownloadOutlined,
} from '@mui/icons-material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  canDownloadCustomerPackage,
  canDownloadExternalSafePackage,
  canDownloadExternalSafeReportPdf,
  canDownloadInternalPackage,
  canDownloadReportArtifactDocxOrCsv,
  canDownloadReportExportFormats,
  canViewTechnicalAppendix,
  getCapabilitiesFromUser,
} from '../auth/access';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  reportsApi,
  type EhsReviewWorksheetPayload,
  type EhsWorksheetItem,
  type ReportVisual,
  type SafetyCaseRecommendationSnapshot,
  type SafetyCaseRecord,
  type SafetyReportArtifactDetailResponse,
  type SafetyReportOpenResponse,
  type WorkflowClaimReadinessResponse,
} from '../services/reportsApi';
import { api } from '../services/api';
import {
  MEASURED_SIGNALS_EXPLANATION,
  MEASURED_SIGNALS_LABEL,
  RATE_WITHHELD_INSUFFICIENT_COVERAGE,
  formatMeasuredSignalsSentence,
  remapSafetyScanCustomerLabel,
} from '../domain/acceptance/safetyScanPresentationCopy';
import {
  buildBootstrapArtifactDetail,
  buildEvidenceSummary,
  hasRenderableArtifactPayload,
  resolveArtifactClaimBoundaryPresentation,
  resolveFrozenArtifactClaimBoundary,
  resolveReportScopeWindow,
  type EvidenceSummary,
} from './SafetyReportArtifactDetail.helpers';
import { SafetyReportArtifactAdvancedBanner } from './SafetyReportArtifactAdvancedBanner';
import ArtifactDetailSectionsSkeleton from '../components/safetyReports/ArtifactDetailSectionsSkeleton';
import { isEffectivenessClaimAllowedFromBoundary } from '../domain/claimReadinessPresentation';
import { BuyerReportSection } from './BuyerReportSection';
import { ComparisonReadinessSection } from './ComparisonReadinessSection';
import { CorrectiveActionImpactTimelineSection, ObservationCoverageBand } from './CorrectiveActionImpactTimelineSection';
import {
  buildCorrectiveActionImpactTimeline,
  type CorrectiveActionImpactTimeline,
} from './SafetyReportArtifactDetail.impactTimeline';
import {
  resolveSafetyReportOutcomeContext,
  type SafetyReportOutcomeContext,
} from './safetyReportOutcomeContext';
import { DecisionReadinessSection } from './DecisionReadinessSection';
import WorkplaceConditionObservedSection, {
  resolveWorkplaceConditionFromArtifact,
} from '../components/safetyReports/WorkplaceConditionObservedSection';
import { buildWorkplaceConditionContextFromArtifact } from '../domain/workplaceCondition/workplaceConditionContext';
import ClosureSummaryArtifactSection from '../components/safetyReports/ClosureSummaryArtifactSection';
import ArtifactReportAlignmentCards from '../components/safetyReports/ArtifactReportAlignmentCards';
import MeasurementAssurancePanel from '../components/safetyReports/MeasurementAssurancePanel';
import { SafetyControlReviewV9ParitySection } from '../components/reports/SafetyControlReviewV9ParitySection';
import { buildRepeatSafetyScanPath } from '../domain/safetyCases/safetyScanWorkspaceHandoff';
import { usesCompactBuyerFace } from '../domain/reportContext/compactBuyerFace';
import { resolveSignedScanArtifactReturnPaths } from '../domain/safetyCases/workflowGlueNavigation';
import { ArtifactOpenLinkedSafetyCaseButton, ArtifactReviewPatternButton } from './artifactWorkflowReturnControls';
import { SafetyManagementLineageBreadcrumbs } from '../components/safetyReports/SafetyManagementLineageBreadcrumbs';

function firstNonEmpty(...values: Array<string | null | undefined>): string {
  for (const value of values) {
    const normalized = String(value || '').trim();
    if (normalized) return normalized;
  }
  return '';
}

function formatDateTime(value?: string | null): string {
  const raw = String(value || '').trim();
  if (!raw) return 'Not available';
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return raw;
  return parsed.toLocaleString();
}

function formatCompactDateTime(value?: string | null): string {
  const raw = String(value || '').trim();
  if (!raw) return 'Not available';
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return raw;
  return parsed.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatCompactWindowRange(start?: string | null, end?: string | null): string {
  const from = String(start || '').trim();
  const to = String(end || '').trim();
  if (!from && !to) return 'Window not available';
  if (!from) return formatCompactDateTime(to);
  if (!to) return formatCompactDateTime(from);
  return `${formatCompactDateTime(from)} - ${formatCompactDateTime(to)}`;
}

function formatArrowWindowRange(start?: string | null, end?: string | null): string {
  const from = formatCompactDateTime(start);
  const to = formatCompactDateTime(end);
  if (from === 'Not available' && to === 'Not available') return 'Window not available';
  return `${from} → ${to}`;
}

function earliestWindowTimestamp(...values: Array<string | null | undefined>): string | null {
  let bestValue: string | null = null;
  let bestMs: number | null = null;
  values.forEach((value) => {
    const ms = timestampToMillis(value);
    if (ms === null) return;
    if (bestMs === null || ms < bestMs) {
      bestMs = ms;
      bestValue = String(value || '').trim() || null;
    }
  });
  return bestValue;
}

function latestWindowTimestamp(...values: Array<string | null | undefined>): string | null {
  let bestValue: string | null = null;
  let bestMs: number | null = null;
  values.forEach((value) => {
    const ms = timestampToMillis(value);
    if (ms === null) return;
    if (bestMs === null || ms > bestMs) {
      bestMs = ms;
      bestValue = String(value || '').trim() || null;
    }
  });
  return bestValue;
}

function formatTokenLabel(value?: string | null): string {
  const raw = String(value || '').trim();
  if (!raw) return 'Not available';
  return raw.replace(/[_-]+/g, ' ').replace(/\b\w/g, (match) => match.toUpperCase());
}

function formatNumericValue(value: unknown, digits = 1): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '0';
  return value.toLocaleString(undefined, {
    minimumFractionDigits: Number.isInteger(value) ? 0 : digits,
    maximumFractionDigits: digits,
  });
}

function asFiniteNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function firstFiniteNumber(...values: Array<number | null | undefined>): number | null {
  for (const value of values) {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
  }
  return null;
}

function splitWindowRange(value?: string | null): { start: string; end: string } {
  const raw = String(value || '').trim();
  if (!raw) return { start: '', end: '' };
  const parts = raw.split(/\s+to\s+/i);
  if (parts.length >= 2) {
    return {
      start: String(parts[0] || '').trim(),
      end: String(parts.slice(1).join(' to ') || '').trim(),
    };
  }
  return { start: raw, end: '' };
}

function timestampToMillis(value?: string | null): number | null {
  const raw = String(value || '').trim();
  if (!raw) return null;
  const parsed = Date.parse(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

function durationBetween(start?: string | null, end?: string | null): number | null {
  const from = timestampToMillis(start);
  const to = timestampToMillis(end);
  if (from === null || to === null) return null;
  return Math.max(0, to - from);
}

const DURATION_COMPARISON_TOLERANCE_MS = 60_000;

function durationsMatchWithinTolerance(
  leftDurationMs: number | null,
  rightDurationMs: number | null,
): boolean {
  if (leftDurationMs === null || rightDurationMs === null) return false;
  return Math.abs(leftDurationMs - rightDurationMs) <= DURATION_COMPARISON_TOLERANCE_MS;
}

function durationsDifferBeyondTolerance(
  leftDurationMs: number | null,
  rightDurationMs: number | null,
): boolean {
  if (leftDurationMs === null || rightDurationMs === null) return false;
  return !durationsMatchWithinTolerance(leftDurationMs, rightDurationMs);
}

function formatDurationCompact(durationMs: number | null): string {
  if (durationMs === null) return 'Window not available';
  const totalMinutes = Math.floor(durationMs / 60000);
  const days = Math.floor(totalMinutes / (24 * 60));
  const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
  const minutes = totalMinutes % 60;
  if (days > 0) {
    return `${days}d ${String(hours).padStart(2, '0')}h ${String(minutes).padStart(2, '0')}m`;
  }
  if (hours > 0) {
    return `${hours}h ${String(minutes).padStart(2, '0')}m`;
  }
  return `${minutes}m`;
}

function extractAreaDetail(value?: string | null): string {
  const raw = String(value || '').trim();
  if (!raw) return '';
  return raw.replace(/^Area:\s*/i, '').trim();
}

function normalizeOptionalValue(value: unknown): string {
  const raw = String(value || '').trim();
  if (!raw || raw.toLowerCase() === 'none' || raw.toLowerCase() === 'null') return '';
  return raw;
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const VISUAL_TONES = {
  blue: { accent: '#2563eb', soft: '#eff6ff', border: '#bfdbfe' },
  orange: { accent: '#f97316', soft: '#fff7ed', border: '#fdba74' },
  green: { accent: '#16a34a', soft: '#ecfdf5', border: '#86efac' },
  amber: { accent: '#d97706', soft: '#fffbeb', border: '#fcd34d' },
  red: { accent: '#dc2626', soft: '#fef2f2', border: '#fca5a5' },
  purple: { accent: '#7c3aed', soft: '#f5f3ff', border: '#c4b5fd' },
  neutral: { accent: '#64748b', soft: '#f8fafc', border: '#cbd5e1' },
} as const;

const MIN_MONITORED_HOURS_FOR_TREND = 2.0;

const IMPACT_INTERPRETATION_WITHHELD_COPY =
  `${RATE_WITHHELD_INSUFFICIENT_COVERAGE}. Current counts are shown for audit context only. This scan does not claim that a control worked or that risk was reduced.`;

function isImpactDenominatorTrusted(trust: string): boolean {
  return String(trust || '').trim().toLowerCase() === 'trusted';
}

type RateInterpretationTone = 'improved' | 'worsened' | 'neutral';

function resolveRateInterpretationTone(
  baselineRate: number | null,
  followupRate: number | null,
  denominatorTrust: string,
  percentReduction: number | null,
): RateInterpretationTone {
  if (!isImpactDenominatorTrusted(denominatorTrust)) {
    return 'neutral';
  }
  if (baselineRate === null || followupRate === null) {
    return 'neutral';
  }
  if (typeof percentReduction === 'number') {
    if (percentReduction > 0) return 'improved';
    if (percentReduction < 0) return 'worsened';
    return 'neutral';
  }
  if (followupRate < baselineRate) return 'improved';
  if (followupRate > baselineRate) return 'worsened';
  return 'neutral';
}

function rateInterpretationToneColors(tone: RateInterpretationTone) {
  if (tone === 'improved') return VISUAL_TONES.green;
  if (tone === 'worsened') return VISUAL_TONES.red;
  return VISUAL_TONES.amber;
}

function buildShortWindowWarning(
  baselineMonitoredHours: number | null,
  followupMonitoredHours: number | null,
  threshold = MIN_MONITORED_HOURS_FOR_TREND,
): string | null {
  const baselineShort = baselineMonitoredHours !== null && baselineMonitoredHours < threshold;
  const followupShort = followupMonitoredHours !== null && followupMonitoredHours < threshold;
  if (!baselineShort && !followupShort) {
    return null;
  }
  return 'Short-window warning: baseline and follow-up windows are below the recommended monitored duration for trend interpretation.';
}

function buildObservationTrustExplanation(props: {
  denominatorTrust: string;
  denominatorBasis: string;
  baselineMonitoredHours: number | null;
  followupMonitoredHours: number | null;
  baselineCoveragePercent: number | null;
  followupCoveragePercent: number | null;
  measurementNotes: string[];
  minMonitoredHours?: number;
}): string | null {
  const {
    denominatorTrust,
    denominatorBasis,
    baselineMonitoredHours,
    followupMonitoredHours,
    baselineCoveragePercent,
    followupCoveragePercent,
    measurementNotes,
    minMonitoredHours = MIN_MONITORED_HOURS_FOR_TREND,
  } = props;
  if (isImpactDenominatorTrusted(denominatorTrust)) {
    return null;
  }
  const notesJoined = measurementNotes.join(' ').toLowerCase();
  const coverageComplete = (baselineCoveragePercent ?? 0) >= 99.5 && (followupCoveragePercent ?? 0) >= 99.5;
  const monitoredDurationShort = (
    (baselineMonitoredHours !== null && baselineMonitoredHours < minMonitoredHours)
    || (followupMonitoredHours !== null && followupMonitoredHours < minMonitoredHours)
  );
  if (coverageComplete && monitoredDurationShort) {
    return 'Coverage is near-complete, but monitored-time reliability still needs review before a customer-facing effectiveness claim can be made.';
  }
  if (denominatorBasis === 'elapsed_wall_clock' || notesJoined.includes('demo note')) {
    return 'Matched comparison windows are shown for traceability, but monitored-time reliability still needs review before a verified effectiveness conclusion can be made.';
  }
  if (
    notesJoined.includes('heartbeat')
    || notesJoined.includes('pipeline running/stopped')
    || notesJoined.includes('not validated')
  ) {
    return 'Monitored hours were derived from heartbeat or device-activity coverage rather than a validated pipeline running/stopped record, so denominator trust remains under review.';
  }
  if (coverageComplete && denominatorTrust === 'needs_review') {
    return 'Coverage is near-complete, but denominator trust remains under review until monitored-time evidence is claim-qualified for this comparison.';
  }
  return null;
}

type VisualTone = keyof typeof VISUAL_TONES;
type VisualToneColors = (typeof VISUAL_TONES)[VisualTone];
type ArtifactGranularity = 'hour' | 'day' | 'week';
type ImpactGraphPayload = NonNullable<SafetyReportArtifactDetailResponse['impact_measurement_graph']>;
type EvidenceGalleryPayload = NonNullable<SafetyReportArtifactDetailResponse['evidence_gallery']>;
type EvidenceGalleryItem = NonNullable<EvidenceGalleryPayload['items']>[number];
type RecommendationSnapshotItem = NonNullable<SafetyCaseRecommendationSnapshot['recommendations']>[number];
type WindowPlacement = {
  leftPct: number;
  widthPct: number;
  startPct: number;
  endPct: number;
};

function clampPercent(value: number): number {
  return Math.max(0, Math.min(100, value));
}

function computeWindowPlacement(
  scopeStart?: string | null,
  scopeEnd?: string | null,
  windowStart?: string | null,
  windowEnd?: string | null,
): WindowPlacement | null {
  const reportStart = timestampToMillis(scopeStart);
  const reportEnd = timestampToMillis(scopeEnd);
  const windowFrom = timestampToMillis(windowStart);
  const windowTo = timestampToMillis(windowEnd);
  if (reportStart === null || reportEnd === null || windowFrom === null || windowTo === null || reportEnd <= reportStart) {
    return null;
  }

  const totalScopeMs = reportEnd - reportStart;
  const clampedStart = Math.max(reportStart, Math.min(windowFrom, reportEnd));
  const clampedEnd = Math.max(clampedStart, Math.min(windowTo, reportEnd));
  const baselineLeftPct = ((clampedStart - reportStart) / totalScopeMs) * 100;
  const baselineWidthPct = ((clampedEnd - clampedStart) / totalScopeMs) * 100;

  return {
    leftPct: clampPercent(baselineLeftPct),
    widthPct: clampPercent(baselineWidthPct),
    startPct: clampPercent((clampedStart - reportStart) / totalScopeMs * 100),
    endPct: clampPercent((clampedEnd - reportStart) / totalScopeMs * 100),
  };
}

function describeDurationSlice(durationMs: number | null): string {
  if (durationMs === null) return 'matched';
  const totalMinutes = Math.max(0, Math.floor(durationMs / 60000));
  if (totalMinutes >= 24 * 60) {
    return formatDurationCompact(durationMs);
  }
  if (totalMinutes > 0 && totalMinutes % 60 === 0) {
    const hours = totalMinutes / 60;
    return `${hours}-hour`;
  }
  return formatDurationCompact(durationMs);
}

function comparisonWindowTrackStart(
  baselineStart?: string | null,
  followupStart?: string | null,
): string | null {
  return earliestWindowTimestamp(baselineStart, followupStart);
}

function comparisonWindowTrackEnd(
  baselineEnd?: string | null,
  followupEnd?: string | null,
): string | null {
  return latestWindowTimestamp(baselineEnd, followupEnd);
}

function windowSpanDiffersFromComparison(
  scopeStart?: string | null,
  scopeEnd?: string | null,
  comparisonStart?: string | null,
  comparisonEnd?: string | null,
): boolean {
  const scopeStartMs = timestampToMillis(scopeStart);
  const scopeEndMs = timestampToMillis(scopeEnd);
  const comparisonStartMs = timestampToMillis(comparisonStart);
  const comparisonEndMs = timestampToMillis(comparisonEnd);
  if (scopeStartMs === null || scopeEndMs === null || comparisonStartMs === null || comparisonEndMs === null) {
    return false;
  }
  const toleranceMs = 60_000;
  return scopeStartMs < comparisonStartMs - toleranceMs || scopeEndMs > comparisonEndMs + toleranceMs;
}

function isArtifactGranularity(value: unknown): value is ArtifactGranularity {
  return value === 'hour' || value === 'day' || value === 'week';
}

function formatChartMetric(value: unknown): string {
  return typeof value === 'number' && Number.isFinite(value) ? formatNumericValue(value) : '0';
}

function visualItems(visual?: ReportVisual | null): Array<Record<string, any>> {
  const items = visual?.data?.items;
  return Array.isArray(items) ? items : [];
}

function visualItemById(visual: ReportVisual | null | undefined, id: string): Record<string, any> | null {
  return visualItems(visual).find((item) => String(item?.id || '').trim() === id) || null;
}

function dailyRateToHourly(value: number | null): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value / 24 : null;
}

function looksLikeRawCustomerId(value?: string | null): boolean {
  const text = String(value || '').trim();
  if (!text) return false;
  if (/^(workflow|finding|report|incident|case|camera|cam|device|edge|zone)[-_:/.][a-z0-9._:-]+$/i.test(text)) {
    return true;
  }
  if (/^INC[-_:][A-Z0-9._:-]+$/i.test(text)) return true;
  if (/^\/api\//i.test(text) || /^https?:\/\//i.test(text)) return true;
  return false;
}

function formatSpecifiedValue(value?: string | null): string {
  const raw = String(value || '').trim();
  if (!raw) return 'Not specified';
  if (looksLikeRawCustomerId(raw)) return 'Retained in appendix only';
  return normalizeOptionalValue(value) || 'Not specified';
}

function formatCountValue(value: number | null, missingText: string): string {
  return typeof value === 'number' ? formatNumericValue(value, 0) : missingText;
}

function buildExecutiveEvidenceSummary(summary: EvidenceSummary): string {
  const hasSignals = typeof summary.observed_signals === 'number';
  const hasSnapshots = typeof summary.snapshots === 'number';
  const hasClips = typeof summary.clips === 'number';
  if (hasSignals) {
    const sentence = formatMeasuredSignalsSentence({
      count: summary.observed_signals,
      snapshots: hasSnapshots ? summary.snapshots : null,
      clips: hasClips ? summary.clips : null,
    });
    if (hasSnapshots && hasClips) return sentence;
    return `${sentence} Media counts are not available in this signed-off package.`;
  }
  if (hasSnapshots || hasClips) {
    const snapshotText = typeof summary.snapshots === 'number'
      ? `${formatNumericValue(summary.snapshots, 0)} snapshots`
      : 'Snapshot count not available';
    const clipText = typeof summary.clips === 'number'
      ? `${formatNumericValue(summary.clips, 0)} clips`
      : 'Clip count not available';
    return `${snapshotText}, ${clipText}. Measured-signal count is not available in this signed-off package.`;
  }
  return 'Evidence counts are not available in the signed-off package.';
}

function buildImpactPostureCopy(props: {
  outcomeContext: SafetyReportOutcomeContext;
  impactWaived: boolean;
  denominatorTrust: string;
  measurementStatus: string;
  normalizedComparisonAvailable: boolean;
  hasImpactData: boolean;
}): string {
  if (props.outcomeContext.isDemoOrLoopedSource) {
    return props.outcomeContext.workflowStatusLabel;
  }
  if (props.impactWaived) {
    return 'Corrective action logged as completed. Effectiveness not yet validated.';
  }
  if (!props.hasImpactData) {
    return 'Corrective action logged as completed. Effectiveness not yet validated.';
  }
  if (props.normalizedComparisonAvailable) {
    return 'Finding supported. Corrective-action effectiveness requires matched follow-up validation.';
  }
  if (props.denominatorTrust === 'needs_review' || props.denominatorTrust === 'insufficient_data') {
    return 'Corrective action logged as completed. Observation-time reliability requires review before stronger claims can be made.';
  }
  if (props.measurementStatus === 'needs_review' || props.measurementStatus === 'insufficient_data') {
    return 'Corrective action logged as completed. Effectiveness not yet validated.';
  }
  return 'Corrective action logged as completed. Effectiveness not yet validated.';
}

function normalizeHourPeriodLabel(label: string): string {
  const match = /^(\d{1,2})\s*(AM|PM)$/i.exec(String(label || '').trim());
  if (!match) return label;
  const hour = Number(match[1]) % 12;
  return `${hour === 0 ? 12 : hour} ${match[2].toUpperCase()}`;
}

function formatZoneDisplayLabel(label: string): string {
  const raw = String(label || '').trim();
  if (!raw) return '';
  const compact = raw.toLowerCase().replace(/[_\-\s]+/g, '');
  // Intentional alias: demo5tes / Demo test area → Restricted Forklift Zone (not Demo test area).
  if (
    compact === 'demo5tes'
    || compact === 'demotestarea'
    || /demo5tes/i.test(raw)
    || /^demo\s*test\s*area$/i.test(raw)
  ) {
    return 'Restricted Forklift Zone';
  }
  // Never passthrough other Demo* area/mount chrome in Scan Report customer surfaces.
  if (/\bdemo\b/i.test(raw) || /^demo[a-z0-9_]/i.test(raw)) {
    return '';
  }
  return raw;
}

function SectionCard(props: {
  title: string;
  subtitle?: string;
  badge?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 3,
        bgcolor: 'background.paper',
        p: { xs: 2, md: 2.5 },
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'flex-start' }}
        sx={{ mb: 1.5 }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontSize: '1.05rem', fontWeight: 700 }}>
            {props.title}
          </Typography>
          {props.subtitle ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {props.subtitle}
            </Typography>
          ) : null}
        </Box>
        {props.badge}
      </Stack>
      {props.children}
    </Box>
  );
}

function MetricTile(props: {
  label: string;
  value: string;
  detail?: string;
  tone?: VisualTone;
  icon: React.ElementType;
  progressPercent?: number | null;
  footerNote?: string;
}) {
  const tone = VISUAL_TONES[props.tone || 'neutral'];
  const Icon = props.icon;
  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: '#E5E7EB',
        borderRadius: 2.5,
        bgcolor: '#fff',
        p: 1.6,
        minHeight: 148,
        boxShadow: '0 4px 14px rgba(15, 23, 42, 0.03)',
      }}
    >
      <Box
        sx={{
          width: 30,
          height: 30,
          borderRadius: '50%',
          bgcolor: tone.soft,
          color: tone.accent,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 1.1,
        }}
      >
        <Icon aria-hidden sx={{ fontSize: 18 }} />
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600 }}>
        {props.label}
      </Typography>
      <Typography variant="h4" sx={{ mt: 0.6, fontSize: { xs: '1.75rem', md: '2rem' }, fontWeight: 700, color: '#0f172a' }}>
        {props.value}
      </Typography>
      {props.detail ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.1 }}>
          {props.detail}
        </Typography>
      ) : null}
      {typeof props.progressPercent === 'number' ? (
        <Box sx={{ mt: 1 }}>
          <Box sx={{ height: 6, borderRadius: 999, bgcolor: '#E5E7EB', overflow: 'hidden' }}>
            <Box sx={{ width: `${Math.max(0, Math.min(props.progressPercent, 100))}%`, height: '100%', bgcolor: tone.accent }} />
          </Box>
          {props.footerNote ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.65 }}>
              {props.footerNote}
            </Typography>
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
}

function InfoNote(props: { message: string }) {
  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="flex-start"
      sx={{
        mt: 1.5,
        px: 1.2,
        py: 1,
        border: '1px solid',
        borderColor: '#E5E7EB',
        borderRadius: 2,
        bgcolor: '#F8FAFC',
      }}
    >
      <InfoOutlined aria-hidden sx={{ fontSize: 16, mt: '2px', color: VISUAL_TONES.blue.accent }} />
      <Typography variant="caption" color="text.secondary">
        {props.message}
      </Typography>
    </Stack>
  );
}

function StatusBadge(props: {
  label: string;
  tone?: 'success' | 'error' | 'warning' | 'info' | 'neutral';
}) {
  const styles = {
    success: { color: '#166534', bgcolor: '#DCFCE7', borderColor: '#86EFAC' },
    error: { color: '#B91C1C', bgcolor: '#FEE2E2', borderColor: '#FCA5A5' },
    warning: { color: '#B45309', bgcolor: '#FEF3C7', borderColor: '#FCD34D' },
    info: { color: '#1D4ED8', bgcolor: '#DBEAFE', borderColor: '#93C5FD' },
    neutral: { color: '#475569', bgcolor: '#F8FAFC', borderColor: '#CBD5E1' },
  } as const;
  const tone = styles[props.tone || 'neutral'];
  return (
    <Chip
      label={props.label}
      size="small"
      sx={{
        height: 24,
        fontWeight: 700,
        fontSize: '0.72rem',
        letterSpacing: '0.01em',
        color: tone.color,
        bgcolor: tone.bgcolor,
        border: '1px solid',
        borderColor: tone.borderColor,
        '& .MuiChip-label': {
          px: 1.05,
        },
      }}
    />
  );
}

function InfoCallout(props: { message: string }) {
  return (
    <Box
      sx={{
        mt: 1.5,
        px: 1.4,
        py: 1.2,
        borderRadius: 2,
        border: '1px solid',
        borderColor: '#BFDBFE',
        bgcolor: '#EFF6FF',
      }}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <InfoOutlined aria-hidden sx={{ fontSize: 18, mt: '2px', color: VISUAL_TONES.blue.accent }} />
        <Typography variant="body2" color="text.secondary">
          {props.message}
        </Typography>
      </Stack>
    </Box>
  );
}

function SummaryCardShell(props: {
  title: string;
  subtitle: string;
  icon: React.ElementType;
  children: React.ReactNode;
}) {
  const Icon = props.icon;
  return (
    <Card sx={{ height: '100%', borderRadius: 3, border: '1px solid', borderColor: '#E5E7EB', boxShadow: '0 8px 24px rgba(15, 23, 42, 0.04)' }}>
      <CardContent sx={{ p: 2.25 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.8 }}>
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              bgcolor: '#F8FAFC',
              color: VISUAL_TONES.blue.accent,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: '0 0 auto',
            }}
          >
            <Icon aria-hidden sx={{ fontSize: 18 }} />
          </Box>
          <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 700 }}>
            {props.title}
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {props.subtitle}
        </Typography>
        {props.children}
      </CardContent>
    </Card>
  );
}

function InfoRow(props: {
  icon: React.ElementType;
  label: string;
  value: string;
  badge?: React.ReactNode;
  divider?: boolean;
  valueTestId?: string;
  valueDataRaw?: string;
}) {
  const Icon = props.icon;
  return (
    <Stack
      direction="row"
      spacing={1.2}
      alignItems="flex-start"
      sx={{
        py: 1.15,
        borderBottom: props.divider ? '1px solid #E5E7EB' : 'none',
      }}
    >
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: '50%',
          bgcolor: '#F8FAFC',
          color: VISUAL_TONES.neutral.accent,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flex: '0 0 auto',
        }}
      >
        <Icon aria-hidden sx={{ fontSize: 16 }} />
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.75} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            {props.label}
          </Typography>
          {props.badge}
        </Stack>
        <Typography
          variant="body2"
          sx={{ mt: 0.35, fontWeight: 600, color: '#0f172a' }}
          data-testid={props.valueTestId}
          data-raw-value={props.valueDataRaw}
        >
          {props.value || 'Not available'}
        </Typography>
      </Box>
    </Stack>
  );
}

function ActionTile(props: {
  title: string;
  description: string;
  onClick: () => void;
}) {
  return (
    <Button
      fullWidth
      variant="outlined"
      onClick={props.onClick}
      sx={{
        justifyContent: 'space-between',
        alignItems: 'center',
        textTransform: 'none',
        borderColor: '#E5E7EB',
        borderRadius: 2,
        px: 1.5,
        py: 1.2,
      }}
    >
      <Box sx={{ textAlign: 'left' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {props.title}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {props.description}
        </Typography>
      </Box>
      <ArrowOutwardOutlined aria-hidden sx={{ fontSize: 18, color: 'text.secondary' }} />
    </Button>
  );
}

function SafetyScanFindingCard(props: {
  findingText: string;
  riskDescription: string;
  riskBadgeLabel: string;
  riskBadgeTone: 'success' | 'error' | 'warning' | 'info' | 'neutral';
  detectionDate: string;
  location: string;
  triggerText: string;
}) {
  return (
    <SummaryCardShell
      title="Detailed safety scan finding"
      subtitle="What the scan found and why it matters for worker safety."
      icon={ShieldOutlined}
    >
      <InfoRow icon={WarningAmberOutlined} label="Finding" value={props.findingText} divider />
      <InfoRow
        icon={ReportProblemOutlined}
        label="Risk level"
        value={props.riskDescription}
        badge={<StatusBadge label={props.riskBadgeLabel} tone={props.riskBadgeTone} />}
        divider
      />
      <InfoRow icon={CalendarTodayOutlined} label="Detection date" value={props.detectionDate} divider />
      <InfoRow icon={LocationOnOutlined} label="Location" value={props.location} divider />
      <InfoRow icon={VisibilityOutlined} label="Triggers" value={props.triggerText} />
    </SummaryCardShell>
  );
}

function ClaimsConfidenceCard(props: {
  validatedClaim: string;
  keyInsight: string;
  claimConfidence: string;
  evidenceSufficiency: string;
  dataNormalization: string;
  comparisonMethod: string;
  exposureBasis: string;
  callout: string;
}) {
  return (
    <SummaryCardShell
      title="Claims and confidence"
      subtitle="What we can and cannot claim based on the available evidence."
      icon={GppGoodOutlined}
    >
      <InfoRow icon={FactCheckOutlined} label="Validated claim" value={props.validatedClaim} divider />
      <InfoRow icon={InfoOutlined} label="Key insight" value={props.keyInsight} divider />
      <InfoRow icon={GppGoodOutlined} label="Claim confidence" value={props.claimConfidence} badge={<StatusBadge label={props.claimConfidence} tone="info" />} divider />
      <InfoRow icon={CheckCircleOutlineOutlined} label="Evidence sufficiency" value={props.evidenceSufficiency} badge={<StatusBadge label={props.evidenceSufficiency} tone={props.evidenceSufficiency === 'Not available' ? 'warning' : 'success'} />} divider />
      <InfoRow icon={AssessmentOutlined} label="Data normalization" value={props.dataNormalization} badge={<StatusBadge label={props.dataNormalization} tone="info" />} divider />
      <InfoRow icon={CompareArrowsOutlined} label="Comparison method" value={props.comparisonMethod} divider />
      <InfoRow icon={AccessTimeOutlined} label="Exposure basis" value={props.exposureBasis} />
      <InfoCallout message={props.callout} />
    </SummaryCardShell>
  );
}

function worksheetStatusLabel(status: string): string {
  const normalized = String(status || '').trim().toLowerCase();
  if (normalized === 'answered') return 'Answered';
  if (normalized === 'partially_answered') return 'Partially answered';
  if (normalized === 'requires_more_data') return 'Requires more data';
  return 'Not available';
}

function worksheetStatusTone(status: string): 'success' | 'warning' | 'info' | 'neutral' {
  const normalized = String(status || '').trim().toLowerCase();
  if (normalized === 'answered') return 'success';
  if (normalized === 'partially_answered') return 'info';
  if (normalized === 'requires_more_data') return 'warning';
  return 'neutral';
}

function worksheetOverallStatusLabel(status: string): string {
  const normalized = String(status || '').trim().toLowerCase();
  if (normalized === 'decision_ready') return 'Decision-ready';
  if (normalized === 'partially_decision_ready') return 'Partially decision-ready';
  return 'Not decision-ready';
}

function EhsWorksheetQuestionRow(props: { item: EhsWorksheetItem }) {
  const status = String(props.item.status || '').trim().toLowerCase();
  return (
    <Box sx={{ py: 1.1, borderBottom: '1px solid', borderColor: '#E5E7EB' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
        <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>
          {formatSpecifiedValue(props.item.question)}
        </Typography>
        <StatusBadge label={worksheetStatusLabel(status)} tone={worksheetStatusTone(status)} />
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
        {formatSpecifiedValue(props.item.answer)}
      </Typography>
      {Array.isArray(props.item.evidence_source) && props.item.evidence_source.length ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          {`Evidence source: ${props.item.evidence_source.join(', ')}`}
        </Typography>
      ) : null}
      {Array.isArray(props.item.missing_inputs) && props.item.missing_inputs.length ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.6 }}>
          {`Missing inputs: ${props.item.missing_inputs.join(', ')}`}
        </Typography>
      ) : null}
      {normalizeOptionalValue(props.item.recommended_followup) ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.35 }}>
          {props.item.recommended_followup}
        </Typography>
      ) : null}
    </Box>
  );
}

function EhsReviewWorksheetCard(props: { worksheet?: EhsReviewWorksheetPayload | null }) {
  const worksheet = props.worksheet || null;
  const questions = Array.isArray(worksheet?.questions) ? worksheet?.questions || [] : [];
  const statusCounts = worksheet?.status_counts || {};
  const groups: Array<{ title: string; ids: string[] }> = [
    { title: 'Trend and change', ids: ['what_changed', 'trend_stability', 'process_shift'] },
    {
      title: 'Concentration and drivers',
      ids: ['concentration', 'top_drivers', 'best_worst_comparison', 'best_performer_practices'],
    },
    {
      title: 'Severe exposure and controls',
      ids: ['severe_exposure', 'current_controls', 'highest_control', 'action_practicality'],
    },
    {
      title: 'Defensibility and ethics',
      ids: ['ethical_defensibility', 'scrutiny_defensibility', 'professional_responsibility'],
    },
    {
      title: 'Action ownership and verification',
      ids: ['action_owner', 'action_timing', 'verification_method'],
    },
  ];

  return (
    <Card sx={{ mb: 3, borderRadius: 3, boxShadow: '0 8px 24px rgba(15, 23, 42, 0.04)' }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          EHS review worksheet
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Decision-completeness review based on the signed-off scan evidence.
        </Typography>
        {!worksheet ? (
          <Alert severity="info">EHS review worksheet was not returned for this Signed Safety Scan.</Alert>
        ) : (
          <Stack spacing={2}>
            <Box sx={{ border: '1px solid', borderColor: '#E5E7EB', borderRadius: 2.5, p: 1.75, bgcolor: '#F8FAFC' }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.5}>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {typeof worksheet.completeness_score_percent === 'number'
                      ? `Decision completeness: ${formatNumericValue(worksheet.completeness_score_percent)}%`
                      : 'Decision completeness requires review'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {normalizeOptionalValue(worksheet.summary) || 'Worksheet summary not included in signed-off package.'}
                  </Typography>
                </Box>
                <Chip
                  label={worksheetOverallStatusLabel(String(worksheet.overall_status || ''))}
                  color={String(worksheet.overall_status || '').includes('decision_ready') && !String(worksheet.overall_status || '').includes('partially') ? 'success' : 'warning'}
                  variant="outlined"
                />
              </Stack>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1.2 }}>
                <StatusBadge label={`${formatNumericValue(Number(statusCounts.answered || 0), 0)} answered`} tone="success" />
                <StatusBadge label={`${formatNumericValue(Number(statusCounts.partially_answered || 0), 0)} partial`} tone="info" />
                <StatusBadge label={`${formatNumericValue(Number(statusCounts.requires_more_data || 0), 0)} requires data`} tone="warning" />
                <StatusBadge label={`${formatNumericValue(Number(statusCounts.not_available || 0), 0)} not included`} tone="neutral" />
              </Stack>
            </Box>
            {groups.map((group) => {
              const items = group.ids
                .map((id) => questions.find((question) => question.id === id))
                .filter((item): item is EhsWorksheetItem => Boolean(item));
              if (!items.length) {
                return null;
              }
              const answeredCount = items.filter(
                (item) => String(item.status || '').trim().toLowerCase() === 'answered',
              ).length;
              const allAnswered = answeredCount === items.length;
              return (
                <Accordion
                  key={group.title}
                  defaultExpanded={false}
                  sx={{ border: '1px solid', borderColor: '#E5E7EB', borderRadius: '12px !important', boxShadow: 'none', '&:before': { display: 'none' } }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreOutlined />}>
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ width: '100%', pr: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{group.title}</Typography>
                      <StatusBadge
                        label={`${answeredCount}/${items.length} answered`}
                        tone={allAnswered ? 'success' : 'warning'}
                      />
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0 }}>
                    {items.map((item) => (
                      <EhsWorksheetQuestionRow key={String(item.id)} item={item} />
                    ))}
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

function SignedOffScopeCard(props: {
  site: string;
  area: string;
  activityType: string;
  eventBasis: string;
  scopeRef: string;
  startTime: string;
  startTimeRaw?: string | null;
  endTime: string;
  endTimeRaw?: string | null;
}) {
  return (
    <SummaryCardShell
      title="Signed-off scan scope"
      subtitle="The immutable Signed Safety Scan scope retained in this artifact."
      icon={EventNoteOutlined}
    >
      <InfoRow icon={BusinessOutlined} label="Site" value={props.site} divider />
      <InfoRow icon={PlaceOutlined} label="Operating area" value={props.area} divider />
      <InfoRow icon={CategoryOutlined} label="Activity type" value={props.activityType} divider />
      <InfoRow icon={VisibilityOutlined} label="Event basis" value={props.eventBasis} divider />
      <InfoRow icon={Inventory2Outlined} label="Scope reference" value={props.scopeRef} divider />
      <InfoRow icon={AccessTimeOutlined} label="Start time" value={props.startTime} valueTestId="signed-off-scope-start-time" valueDataRaw={props.startTimeRaw || ''} divider />
      <InfoRow icon={AccessTimeOutlined} label="End time" value={props.endTime} valueTestId="signed-off-scope-end-time" valueDataRaw={props.endTimeRaw || ''} />
    </SummaryCardShell>
  );
}

function ReportPackageCard(props: {
  preparationStatus: string;
  reportStatus: string;
  packageType: string;
  packageFiles: string;
  onDownloadZip: () => void;
  downloadDisabled?: boolean;
  downloadLoading?: boolean;
  showDownloadButton?: boolean;
}) {
  return (
    <SummaryCardShell
      title="Report package"
      subtitle="Signed-off artifact packaging and delivery status."
      icon={Inventory2Outlined}
    >
      <InfoRow icon={TaskAltOutlined} label="Preparation status" value={props.preparationStatus} badge={<StatusBadge label={props.preparationStatus} tone="success" />} divider />
      <InfoRow icon={FactCheckOutlined} label="Report status" value={props.reportStatus} badge={<StatusBadge label={props.reportStatus} tone="success" />} divider />
      <InfoRow icon={CategoryOutlined} label="Package type" value={props.packageType} divider />
      <InfoRow icon={Inventory2Outlined} label="Package files" value={props.packageFiles} />
      {props.showDownloadButton !== false ? (
      <Button
        fullWidth
        variant="outlined"
        onClick={props.onDownloadZip}
        disabled={props.downloadDisabled}
        sx={{ mt: 1.5, textTransform: 'none', borderRadius: 2 }}
      >
        {props.downloadLoading ? 'Downloading Full Package...' : 'Download Full Package (ZIP)'}
      </Button>
      ) : null}
    </SummaryCardShell>
  );
}

function FollowUpActivityCard(props: {
  hasNewerActivity: boolean;
  latestStatus: string;
  latestTime: string;
  reviewStatus: string;
  recentChanges: string;
}) {
  return (
    <SummaryCardShell
      title="Follow-up workflow activity"
      subtitle="Newer live workflow activity appears only here and does not change the signed-off artifact."
      icon={TrendingUpOutlined}
    >
      <InfoCallout
        message={props.hasNewerActivity
          ? 'Newer live workflow activity exists, but this artifact remains a signed-off and unchanged.'
          : 'No newer workflow activity exists after this signed-off artifact.'}
      />
      <InfoRow icon={TrackChangesOutlined} label="Latest activity status" value={props.latestStatus} divider />
      <InfoRow icon={AccessTimeOutlined} label="Latest activity time" value={props.latestTime} divider />
      <InfoRow icon={FactCheckOutlined} label="Current review status" value={props.reviewStatus} divider />
      <InfoRow icon={AssessmentOutlined} label="Recent changes" value={props.recentChanges} />
    </SummaryCardShell>
  );
}

function ValidationHandoffCard(props: {
  blockers: string[];
  liveWorkflowPath?: string;
  onOpenWorkflow?: () => void;
  liveStatus?: string;
  liveRecentChanges?: string;
}) {
  const blockerCount = props.blockers.length;
  const historicalSummary = blockerCount
    ? `${blockerCount} item${blockerCount === 1 ? '' : 's'} still need follow-up before a stronger impact claim can be made. Continue in the live workflow to clear them before generating the next report.`
    : 'No material follow-up blockers were retained at sign-off. Any new validation work should continue in the live workflow.';
  return (
    <Card sx={{ mb: 3, borderRadius: 3, boxShadow: '0 8px 24px rgba(15, 23, 42, 0.04)' }}>
      <CardContent>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          sx={{ mb: 1.5 }}
        >
          <Box>
            <Typography component="h2" variant="h6" sx={{ mb: 0.4 }}>
              Follow-up plan
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This Signed Safety Scan will not change if the workflow is updated. Use review on the Safety Case to complete matched follow-up validation and prepare the next Safety Scan.
            </Typography>
          </Box>
          <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
          <Chip icon={<LockOutlined />} label="Signed-off artifact" size="small" variant="outlined" />
            {blockerCount ? (
              <Chip
                label={`${blockerCount} blocker${blockerCount === 1 ? '' : 's'}`}
                size="small"
                color="warning"
                variant="outlined"
              />
            ) : null}
          </Stack>
        </Stack>

        <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>
          {historicalSummary}
        </Typography>

        {(props.liveStatus || props.liveRecentChanges) ? (
          <Box
            sx={{
              mt: 1.5,
              px: 1.25,
              py: 1,
              borderRadius: 2,
              bgcolor: '#F8FAFC',
              border: '1px solid #E2E8F0',
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {`Current live workflow status: ${props.liveStatus || 'Not available'}`}
            </Typography>
            {props.liveRecentChanges ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.35 }}>
                {`Latest live workflow change: ${props.liveRecentChanges}`}
              </Typography>
            ) : null}
          </Box>
        ) : null}

        {props.liveWorkflowPath && props.onOpenWorkflow ? (
          <Button
            variant="contained"
            size="small"
            endIcon={<ArrowOutwardOutlined />}
            sx={{ mt: 1.5 }}
            onClick={props.onOpenWorkflow}
          >
            Open current workflow
          </Button>
        ) : null}
      </CardContent>
    </Card>
  );
}

function QuickActionsCard(props: {
  onOpenWorkflow?: () => void;
  onBackToReports: () => void;
}) {
  return (
    <SummaryCardShell
      title="Quick actions"
      subtitle="Navigate or explore related workflows."
      icon={BoltOutlined}
    >
      <Stack spacing={1.1}>
        {props.onOpenWorkflow ? (
          <ActionTile
            title="View in Current Workflow"
            description="Open the latest workflow for this finding."
            onClick={props.onOpenWorkflow}
          />
        ) : null}
        <ActionTile
          title="Back to Safety Control"
          description="Return to the reports list."
          onClick={props.onBackToReports}
        />
      </Stack>
    </SummaryCardShell>
  );
}

function buildSafetyCaseLabel(props: {
  activityTypeLabel?: string;
  findingHeadline?: string;
  riskFound?: string;
}): string {
  const activity = normalizeOptionalValue(props.activityTypeLabel);
  if (activity) return activity;
  const headline = normalizeOptionalValue(props.findingHeadline);
  if (headline) return headline;
  return normalizeOptionalValue(props.riskFound) || 'Safety scan finding';
}

function buildRepresentativeEvidenceNote(props: {
  activityTypeLabel?: string;
  areaLabel?: string;
  findingHeadline?: string;
}): string {
  const activity = normalizeOptionalValue(props.activityTypeLabel);
  const area = formatZoneDisplayLabel(normalizeOptionalValue(props.areaLabel));
  if (activity) {
    const areaPhrase = area ? ` inside the ${area.toLowerCase()}` : '';
    return `Representative evidence of ${activity.toLowerCase()}${areaPhrase}.`;
  }
  const headline = normalizeOptionalValue(props.findingHeadline);
  if (headline) {
    return `Representative evidence supporting the signed-off finding: ${headline}`;
  }
  return 'Representative evidence retained from the signed-off safety scan scope.';
}

function ExecutiveDecisionSummaryCard(props: {
  riskFound: string;
  severeExposure: string;
  evidence: string;
  impactPosture: string;
  decisionReadiness: string;
  nextAction: string;
  traceabilitySource: string;
  safetyCaseLabel: string;
  findingSupport?: string;
}) {
  return (
    <Card sx={{ mb: 3, borderRadius: 3, border: '1px solid #E2E8F0', boxShadow: 'none', bgcolor: '#FFFFFF' }}>
      <CardContent sx={{ p: { xs: 2.2, md: 2.6 } }}>
        <Typography component="h2" variant="h6" sx={{ mb: 0.5, fontWeight: 700 }}>
          Executive summary
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.75 }}>
          What happened, how serious it is, what to do next, and what this report can claim.
        </Typography>
        <Box sx={{ mb: 1.75, p: 1.6, borderRadius: 2.5, bgcolor: '#FEF2F2', border: '1px solid #FECACA' }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', mb: 0.5 }}>
            Safety Case
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a', mb: 1 }}>
            {props.safetyCaseLabel}
          </Typography>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.45 }}>
            Primary finding
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 700, color: '#0f172a' }}>
            {props.riskFound}
          </Typography>
          {props.findingSupport ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.65 }}>
              {props.findingSupport}
            </Typography>
          ) : null}
        </Box>
        <Box
          sx={{
            display: 'grid',
            gap: 1.4,
            gridTemplateColumns: { xs: '1fr', xl: 'repeat(3, minmax(0, 1fr))' },
          }}
        >
          <InfoRow icon={Inventory2Outlined} label="Observed activity" value={props.evidence} divider />
          <InfoRow icon={BoltOutlined} label="Recommended next action" value={props.nextAction} divider />
          <InfoRow icon={GppGoodOutlined} label="Outcome status" value={props.impactPosture} />
        </Box>
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1.5 }}>
          {`Traceability source: ${props.traceabilitySource}`}
        </Typography>
      </CardContent>
    </Card>
  );
}

function EvidenceCoverageCard(props: { summary: EvidenceSummary }) {
  const observedValue = props.summary.observed_signals;
  const snapshotCoverage = props.summary.media_coverage_snapshot_percent;
  const clipCoverage = props.summary.media_coverage_clip_percent;
  const coverageNote = typeof observedValue === 'number' && observedValue > 0
    ? MEASURED_SIGNALS_EXPLANATION
    : typeof observedValue === 'number' && observedValue === 0
      ? `${formatMeasuredSignalsSentence({ count: 0 })} Evidence clip and snapshot counts are not the same as measured-signal counts.`
      : 'Media coverage percent is withheld because measured-signal count is not included in the signed-off package.';

  return (
    <SectionCard
      title="Evidence coverage"
      subtitle="Measured signals in this window, plus snapshot and clip samples. Evidence counts are not observation counts."
    >
      <Box
        sx={{
          display: 'grid',
          gap: 1.25,
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
        }}
      >
        <MetricTile
          icon={VisibilityOutlined}
          label={MEASURED_SIGNALS_LABEL}
          value={
            typeof observedValue === 'number' && observedValue === 0
              ? 'None in window'
              : formatCountValue(props.summary.observed_signals, 'Count not available')
          }
          detail={props.summary.observed_signals === null ? 'Measured-signal count not available in artifact.' : (observedValue === 0 ? 'Not a completed zero-event finding.' : undefined)}
          tone="blue"
        />
        <MetricTile
          icon={ImageOutlined}
          label="Representative snapshots"
          value={formatCountValue(props.summary.snapshots, 'Count not available')}
          detail={
            typeof snapshotCoverage === 'number'
              ? `${formatNumericValue(snapshotCoverage)}% coverage`
              : props.summary.snapshots === null
              ? 'Snapshot count not available in artifact.'
              : 'Coverage withheld pending measured-signal denominator.'
          }
          progressPercent={snapshotCoverage}
          tone="green"
        />
        <MetricTile
          icon={VideocamOutlined}
          label="Representative clips"
          value={formatCountValue(props.summary.clips, 'Count not available')}
          detail={
            typeof clipCoverage === 'number'
              ? `${formatNumericValue(clipCoverage)}% coverage`
              : props.summary.clips === null
              ? 'Clip count not available in artifact.'
              : 'Coverage withheld pending measured-signal denominator.'
          }
          progressPercent={clipCoverage}
          tone="purple"
        />
      </Box>
      <InfoNote message={coverageNote} />
    </SectionCard>
  );
}

function ImpactMeasurementCard(props: {
  visual?: ReportVisual | null;
  graph?: ImpactGraphPayload | null;
  selectedGranularity: ArtifactGranularity;
  onSelectGranularity: (value: ArtifactGranularity) => void;
  comparisonNote?: string;
  claimSupported?: boolean;
  impactWaived?: boolean;
}) {
  const graph = props.graph || null;
  const activeGranularity = props.selectedGranularity || 'hour';
  const graphPoints = Array.isArray(graph?.series?.[activeGranularity]) ? graph?.series?.[activeGranularity] || [] : [];
  const percentReduction = asFiniteNumber(graph?.summary?.percent_reduction);
  const baselinePoint = graphPoints[0] || null;
  const followupPoint = graphPoints[1] || null;
  const denominatorBasis = String(
    graph?.summary?.denominator_basis || graph?.comparability?.denominator_basis || '',
  ).trim().toLowerCase();
  const denominatorTrust = String(
    graph?.summary?.denominator_trust || graph?.comparability?.denominator_trust || '',
  ).trim().toLowerCase();
  const baselineMonitoredHours = asFiniteNumber(graph?.summary?.baseline_monitored_hours);
  const followupMonitoredHours = asFiniteNumber(graph?.summary?.followup_monitored_hours);
  const baselineCoveragePercent = asFiniteNumber(graph?.summary?.baseline_observation_coverage_percent);
  const followupCoveragePercent = asFiniteNumber(graph?.summary?.followup_observation_coverage_percent);
  const measurementNotes = Array.isArray(graph?.summary?.measurement_notes)
    ? (graph?.summary?.measurement_notes || []).filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
    : [];
  const hasRateComparison = Boolean(
    baselinePoint
    && followupPoint
    && asFiniteNumber(baselinePoint?.value) !== null
    && asFiniteNumber(followupPoint?.value) !== null,
  );
  const normalizedAvailable = Boolean(
    denominatorBasis === 'monitored_observation_time'
    && denominatorTrust === 'trusted'
    && hasRateComparison
    && !props.impactWaived,
  );
  const dayRow = visualItemById(props.visual, 'day');
  const weekRow = visualItemById(props.visual, 'week');
  const rawCountsRow = visualItemById(props.visual, 'events_window');
  const unitSuffix = activeGranularity === 'week' ? '/wk' : activeGranularity === 'day' ? '/day' : '/hr';
  const helperText = normalizeOptionalValue(props.comparisonNote)
    || (denominatorBasis === 'elapsed_wall_clock'
      ? 'This comparison uses matched elapsed-time windows because monitored observation time was not available. Treat the result as an observational signal rather than a validated exposure-rate claim.'
      : denominatorBasis === 'monitored_observation_time' && denominatorTrust === 'trusted'
      ? 'Exposure rates are normalized by monitored observation hours. Offline time is excluded from the denominator.'
      : 'Normalized exposure-rate comparison is not available. Rate withheld · insufficient usable coverage.');
  const badgeLabel = props.claimSupported ? 'Measured improvement' : 'Directional improvement signal';
  const rawBaselineCount = asFiniteNumber(graph?.summary?.baseline_exposure_count) ?? asFiniteNumber(rawCountsRow?.baseline_value);
  const rawFollowupCount = asFiniteNumber(graph?.summary?.post_action_exposure_count) ?? asFiniteNumber(rawCountsRow?.followup_value);
  const rateBasisLabel = activeGranularity === 'week' ? 'weekly' : activeGranularity === 'day' ? 'daily' : 'hourly';
  const baselineElapsedHours = asFiniteNumber(graph?.summary?.baseline_elapsed_hours);
  const followupElapsedHours = asFiniteNumber(graph?.summary?.followup_elapsed_hours);
  const durationsDiffer = baselineElapsedHours !== null && followupElapsedHours !== null
    ? durationsDifferBeyondTolerance(
      baselineElapsedHours * 3_600_000,
      followupElapsedHours * 3_600_000,
    )
    : helperText.toLowerCase().includes('different durations');
  const rateBasisSummary = denominatorBasis === 'monitored_observation_time'
    ? 'Rate basis: monitored observation hours'
    : denominatorBasis === 'elapsed_wall_clock'
    ? 'Rate basis: matched elapsed-time comparison windows'
    : 'Rate basis: monitored observation hours unavailable';
  const comparisonReadinessLabel = denominatorTrust === 'trusted'
    ? 'Claim-qualified'
    : denominatorTrust === 'needs_review'
    ? 'Needs review'
    : denominatorTrust === 'insufficient_data'
    ? 'Incomplete'
    : 'Not available';
  const denominatorTrusted = isImpactDenominatorTrusted(denominatorTrust);
  const shortWindowWarning = buildShortWindowWarning(baselineMonitoredHours, followupMonitoredHours);
  const observationTrustExplanation = buildObservationTrustExplanation({
    denominatorTrust,
    denominatorBasis,
    baselineMonitoredHours,
    followupMonitoredHours,
    baselineCoveragePercent,
    followupCoveragePercent,
    measurementNotes,
  });
  const baselineRateValue = asFiniteNumber(baselinePoint?.value);
  const followupRateValue = asFiniteNumber(followupPoint?.value);
  const interpretationTone = resolveRateInterpretationTone(
    baselineRateValue,
    followupRateValue,
    denominatorTrust,
    percentReduction,
  );
  const followupTone = rateInterpretationToneColors(interpretationTone);
  const auditContextTone = VISUAL_TONES.amber;
  const showAuditContextRateComparison = hasRateComparison && !normalizedAvailable && !props.impactWaived;
  const measurementContext = (
    <Box sx={{ border: '1px solid', borderColor: '#E5E7EB', borderRadius: 2.5, p: 1.5 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        Observation basis
      </Typography>
      <Stack spacing={0.75} sx={{ mt: 1 }}>
        <Stack direction="row" justifyContent="space-between" spacing={2}>
          <Typography variant="body2" color="text.secondary">Rate basis</Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{rateBasisSummary.replace('Rate basis: ', '')}</Typography>
        </Stack>
        <Stack direction="row" justifyContent="space-between" spacing={2}>
          <Typography variant="body2" color="text.secondary">Baseline monitored hours</Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{baselineMonitoredHours === null ? 'Not available' : `${formatNumericValue(baselineMonitoredHours)}h`}</Typography>
        </Stack>
        <Stack direction="row" justifyContent="space-between" spacing={2}>
          <Typography variant="body2" color="text.secondary">Follow-up monitored hours</Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{followupMonitoredHours === null ? 'Not available' : `${formatNumericValue(followupMonitoredHours)}h`}</Typography>
        </Stack>
        <Stack direction="row" justifyContent="space-between" spacing={2}>
          <Typography variant="body2" color="text.secondary">Baseline coverage</Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{baselineCoveragePercent === null ? 'Not available' : `${formatNumericValue(baselineCoveragePercent)}%`}</Typography>
        </Stack>
        <Stack direction="row" justifyContent="space-between" spacing={2}>
          <Typography variant="body2" color="text.secondary">Follow-up coverage</Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{followupCoveragePercent === null ? 'Not available' : `${formatNumericValue(followupCoveragePercent)}%`}</Typography>
        </Stack>
        <Stack direction="row" justifyContent="space-between" spacing={2}>
          <Typography variant="body2" color="text.secondary">Comparison readiness</Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{comparisonReadinessLabel}</Typography>
        </Stack>
      </Stack>
      {observationTrustExplanation ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {observationTrustExplanation}
        </Typography>
      ) : null}
      {(measurementNotes[0] || helperText) ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {measurementNotes[0] || helperText}
        </Typography>
      ) : null}
    </Box>
  );

  const withheldMessage = props.impactWaived
    ? 'Impact measurement was waived for this report. Exposure-rate comparison is not presented.'
    : IMPACT_INTERPRETATION_WITHHELD_COPY;

  const renderRateComparisonGrid = (followupColors: VisualToneColors, auditOnly = false) => (
    <Box
      sx={{
        border: '1px solid',
        borderColor: '#E5E7EB',
        borderRadius: 2.5,
        bgcolor: '#fff',
        p: 1.75,
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
          {auditOnly
            ? `Normalized rate (per ${activeGranularity}) — audit context only`
            : `Normalized rate (per ${activeGranularity})`}
        </Typography>
        {!denominatorTrusted ? (
          <Chip label="Needs review" size="small" color="warning" variant="outlined" />
        ) : null}
      </Stack>
      <Box
        sx={{
          mt: 1,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) auto minmax(0, 1fr)' },
          gap: 1.2,
          alignItems: 'center',
        }}
      >
        <Box sx={{ border: '1px solid', borderColor: VISUAL_TONES.orange.border, borderRadius: 2, p: 1.4, bgcolor: VISUAL_TONES.orange.soft }}>
          <Typography variant="caption" sx={{ color: VISUAL_TONES.orange.accent, fontWeight: 700 }}>
            Baseline
          </Typography>
          <Typography variant="h4" sx={{ mt: 0.5, fontSize: '2rem', fontWeight: 800, color: VISUAL_TONES.orange.accent }}>
            {`${formatChartMetric(baselinePoint?.value)}${unitSuffix}`}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center', color: 'text.secondary' }}>
          <ArrowForwardOutlined aria-hidden />
        </Box>
        <Box sx={{ border: '1px solid', borderColor: followupColors.border, borderRadius: 2, p: 1.4, bgcolor: followupColors.soft }}>
          <Typography variant="caption" sx={{ color: followupColors.accent, fontWeight: 700 }}>
            Follow-up
          </Typography>
          <Typography variant="h4" sx={{ mt: 0.5, fontSize: '2rem', fontWeight: 800, color: followupColors.accent }}>
            {`${formatChartMetric(followupPoint?.value)}${unitSuffix}`}
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  const renderImpactChart = (followupBarColor: string) => (
    <Box sx={{ width: '100%', height: 220, mt: 1 }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={graphPoints.map((point) => ({
            name: point.label || 'Value',
            value: asFiniteNumber(point.value) || 0,
          }))}
          margin={{ top: 8, right: 16, left: 0, bottom: 8 }}
        >
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="name" tickLine={false} axisLine={false} />
          <YAxis tickLine={false} axisLine={false} />
          <Bar dataKey="value" radius={[8, 8, 0, 0]}>
            {graphPoints.map((point, index) => (
              <Cell
                key={`${point.id || point.label || 'point'}-${index}`}
                fill={index === 0 ? VISUAL_TONES.orange.accent : followupBarColor}
              />
            ))}
            <LabelList dataKey="value" position="top" formatter={(value: number) => formatNumericValue(value)} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );

  const followupDisplayTone = normalizedAvailable ? followupTone : auditContextTone;
  const changeToneColor = normalizedAvailable
    ? followupTone.accent
    : showAuditContextRateComparison
    ? auditContextTone.accent
    : VISUAL_TONES.neutral.accent;
  const changeLabel = normalizedAvailable && typeof percentReduction === 'number'
    ? interpretationTone === 'worsened'
      ? `↑ ${formatNumericValue(Math.abs(percentReduction))}%`
      : interpretationTone === 'improved'
      ? `↓ ${formatNumericValue(percentReduction)}%`
      : `${formatNumericValue(percentReduction)}%`
    : 'Needs review';
  const rawCountsPanel = (
    <Box sx={{ border: '1px solid', borderColor: '#E5E7EB', borderRadius: 2.5, p: 1.5 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        Raw event counts (audit context)
      </Typography>
      <Stack spacing={0.75} sx={{ mt: 1 }}>
        <Stack direction="row" justifyContent="space-between" spacing={2}>
          <Typography variant="body2" color="text.secondary">Baseline window</Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{rawBaselineCount === null ? 'Not available' : formatNumericValue(rawBaselineCount, 0)}</Typography>
        </Stack>
        <Stack direction="row" justifyContent="space-between" spacing={2}>
          <Typography variant="body2" color="text.secondary">Follow-up window</Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{rawFollowupCount === null ? 'Not available' : formatNumericValue(rawFollowupCount, 0)}</Typography>
        </Stack>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        {durationsDiffer
          ? 'Raw counts are not directly comparable because the windows have different durations.'
          : 'Raw counts are shown for audit context.'}
      </Typography>
    </Box>
  );
  const granularityTable = (
    <Box sx={{ border: '1px solid', borderColor: '#E5E7EB', borderRadius: 2, overflow: 'hidden' }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'minmax(110px, 1fr) minmax(70px, auto) minmax(70px, auto) minmax(70px, auto)',
          columnGap: 1,
          px: 1.5,
          py: 1,
          bgcolor: '#F8FAFC',
          borderBottom: '1px solid',
          borderColor: '#E5E7EB',
        }}
      >
        <Typography variant="caption" color="text.secondary" />
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>Baseline</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>Follow-up</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>Change</Typography>
      </Box>
      {[dayRow, weekRow].filter(Boolean).map((row) => {
        const rowId = String(row?.id || '');
        return (
          <Box
            key={rowId}
            onClick={() => props.onSelectGranularity(rowId as ArtifactGranularity)}
            sx={{
              display: 'grid',
              gridTemplateColumns: 'minmax(110px, 1fr) minmax(70px, auto) minmax(70px, auto) minmax(70px, auto)',
              columnGap: 1,
              px: 1.5,
              py: 1.1,
              borderBottom: rowId === 'week' ? 'none' : '1px solid',
              borderColor: '#E5E7EB',
              cursor: 'pointer',
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{String(row?.label || 'Metric')}</Typography>
            <Typography variant="body2">{formatChartMetric(row?.baseline_value)}</Typography>
            <Typography variant="body2">{formatChartMetric(row?.followup_value)}</Typography>
            <Typography variant="body2" sx={{ color: changeToneColor, fontWeight: 700 }}>
              {changeLabel}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );

  return (
    <SectionCard title="Detailed impact measurement" subtitle={helperText}>
      {normalizedAvailable || showAuditContextRateComparison ? (
        <Stack spacing={2}>
          {showAuditContextRateComparison ? (
            <>
              <Alert severity="warning" sx={{ borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Impact interpretation withheld
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {withheldMessage}
                </Typography>
              </Alert>
              {shortWindowWarning ? (
                <Alert severity="warning" variant="outlined" sx={{ borderRadius: 2 }}>
                  {shortWindowWarning}
                </Alert>
              ) : null}
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: auditContextTone.border,
                  borderRadius: 2.5,
                  bgcolor: auditContextTone.soft,
                  p: 2,
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: auditContextTone.accent }}>
                  Impact interpretation withheld pending monitored-time review.
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                  No improvement or worsening claim is made.
                </Typography>
              </Box>
            </>
          ) : null}
          <Box
            sx={{
              display: 'grid',
              gap: 1.5,
              gridTemplateColumns: { xs: '1fr', xl: 'minmax(0, 1.45fr) 220px' },
            }}
          >
            {renderRateComparisonGrid(followupDisplayTone, showAuditContextRateComparison)}
            <Stack spacing={1.5}>
              {normalizedAvailable ? (
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: followupTone.border,
                    borderRadius: 2.5,
                    bgcolor: followupTone.soft,
                    p: 2,
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {interpretationTone === 'improved'
                      ? 'Exposure reduced'
                      : interpretationTone === 'worsened'
                      ? 'Exposure increased'
                      : 'No material change'}
                  </Typography>
                  <Typography variant="h3" sx={{ mt: 0.75, fontSize: '2.5rem', fontWeight: 800, color: followupTone.accent }}>
                    {typeof percentReduction === 'number'
                      ? interpretationTone === 'worsened'
                        ? `${formatNumericValue(Math.abs(percentReduction))}%`
                        : `${formatNumericValue(Math.abs(percentReduction))}%`
                      : 'Not available'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Based on normalized {rateBasisLabel} rate
                  </Typography>
                  <Chip label={badgeLabel} size="small" sx={{ mt: 1.1, alignSelf: 'flex-start', bgcolor: 'rgba(255,255,255,0.85)' }} />
                </Box>
              ) : null}
              {rawCountsPanel}
              {measurementContext}
            </Stack>
          </Box>

          <Box sx={{ border: '1px solid', borderColor: '#E5E7EB', borderRadius: 2.5, p: 1.75 }}>
            <Stack spacing={1.5}>
              {granularityTable}
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {`Normalized exposure rate per ${activeGranularity}`}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {showAuditContextRateComparison
                    ? 'Rates shown for audit context only. No improvement or worsening claim is made.'
                    : 'Lower is better.'}
                </Typography>
              </Box>
            </Stack>
            {renderImpactChart(followupDisplayTone.accent)}
          </Box>

          <InfoNote message={durationsDiffer ? `${helperText} Raw event counts are shown for audit context only.` : helperText} />
        </Stack>
      ) : (
        <Stack spacing={1.5}>
          <Alert severity={props.impactWaived ? 'info' : 'warning'} sx={{ borderRadius: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {props.impactWaived ? 'Impact measurement waived' : 'Impact interpretation withheld'}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {withheldMessage || helperText || 'Normalized comparison not available for this Signed Safety Scan.'}
            </Typography>
          </Alert>
          {shortWindowWarning ? (
            <Alert severity="warning" variant="outlined" sx={{ borderRadius: 2 }}>
              {shortWindowWarning}
            </Alert>
          ) : null}
          {measurementContext}
          {rawCountsPanel}
        </Stack>
      )}
    </SectionCard>
  );
}

function WindowDetailRow(props: {
  title: string;
  start?: string | null;
  end?: string | null;
  durationMs: number | null;
  color: VisualTone;
}) {
  const tone = VISUAL_TONES[props.color] || VISUAL_TONES.neutral;
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={1.25}
      alignItems={{ xs: 'flex-start', sm: 'center' }}
      sx={{
        py: 1.15,
        borderTop: '1px solid',
        borderColor: '#E5E7EB',
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: { sm: 180 } }}>
        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: tone.accent, flex: '0 0 auto' }} />
        <Typography variant="body2" sx={{ fontWeight: 700, color: tone.accent }}>
          {props.title}
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 0, wordBreak: 'break-word' }}>
        {formatArrowWindowRange(props.start, props.end)}
      </Typography>
      <Chip
        size="small"
        label={formatDurationCompact(props.durationMs)}
        variant="outlined"
        sx={{
          borderColor: tone.border,
          bgcolor: tone.soft,
          color: tone.accent,
          fontWeight: 700,
          flex: '0 0 auto',
        }}
      />
    </Stack>
  );
}

function UnifiedComparisonTimeline(props: {
  trackStart?: string | null;
  trackEnd?: string | null;
  baselineStart?: string | null;
  baselineEnd?: string | null;
  followupStart?: string | null;
  followupEnd?: string | null;
  pivotLabel?: string | null;
  syntheticPivot?: boolean;
}) {
  const trackStart = props.trackStart;
  const trackEnd = props.trackEnd;
  const baselinePlacement = computeWindowPlacement(trackStart, trackEnd, props.baselineStart, props.baselineEnd);
  const followupPlacement = computeWindowPlacement(trackStart, trackEnd, props.followupStart, props.followupEnd);
  const baselineEndMs = timestampToMillis(props.baselineEnd);
  const followupStartMs = timestampToMillis(props.followupStart);
  const trackStartMs = timestampToMillis(trackStart);
  const trackEndMs = timestampToMillis(trackEnd);
  const splitPositionPct = (() => {
    if (trackStartMs === null || trackEndMs === null || trackEndMs <= trackStartMs) return null;
    const totalScopeMs = trackEndMs - trackStartMs;
    if (baselineEndMs !== null && followupStartMs !== null) {
      return clampPercent((((baselineEndMs + followupStartMs) / 2) - trackStartMs) / totalScopeMs * 100);
    }
    if (baselineEndMs !== null) {
      return clampPercent((baselineEndMs - trackStartMs) / totalScopeMs * 100);
    }
    if (followupStartMs !== null) {
      return clampPercent((followupStartMs - trackStartMs) / totalScopeMs * 100);
    }
    return null;
  })();
  const pivotTimestamp = (() => {
    if (baselineEndMs !== null && followupStartMs !== null) {
      return new Date((baselineEndMs + followupStartMs) / 2).toISOString();
    }
    if (baselineEndMs !== null) return props.baselineEnd || null;
    if (followupStartMs !== null) return props.followupStart || null;
    return null;
  })();
  const midpointTitle = props.syntheticPivot ? 'Signed-off midpoint' : (props.pivotLabel || 'Corrective-action midpoint');

  return (
    <Box sx={{ mt: 2.2 }}>
      <Box sx={{ position: 'relative', height: 54, px: 0.5 }}>
        <Box
          sx={{
            position: 'absolute',
            left: 8,
            right: 8,
            top: 18,
            height: 18,
            borderRadius: 999,
            bgcolor: '#E2E8F0',
          }}
        />
        {baselinePlacement ? (
          <Box
            data-testid="baseline-window-track"
            sx={{
              position: 'absolute',
              left: `calc(8px + (100% - 16px) * ${baselinePlacement.leftPct / 100})`,
              width: `calc((100% - 16px) * ${Math.max(baselinePlacement.widthPct, 8) / 100})`,
              top: 18,
              height: 18,
              borderRadius: 999,
              bgcolor: VISUAL_TONES.orange.accent,
              boxShadow: '0 4px 12px rgba(234, 88, 12, 0.25)',
              zIndex: 1,
            }}
          />
        ) : null}
        {followupPlacement ? (
          <Box
            data-testid="followup-window-track"
            sx={{
              position: 'absolute',
              left: `calc(8px + (100% - 16px) * ${followupPlacement.leftPct / 100})`,
              width: `calc((100% - 16px) * ${Math.max(followupPlacement.widthPct, 8) / 100})`,
              top: 18,
              height: 18,
              borderRadius: 999,
              bgcolor: VISUAL_TONES.green.accent,
              boxShadow: '0 4px 12px rgba(22, 163, 74, 0.25)',
              zIndex: 1,
            }}
          />
        ) : null}
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            top: 16,
            width: 18,
            height: 22,
            borderRadius: '50%',
            bgcolor: '#CBD5E1',
            border: '3px solid #fff',
            zIndex: 2,
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            right: 0,
            top: 16,
            width: 18,
            height: 22,
            borderRadius: '50%',
            bgcolor: '#CBD5E1',
            border: '3px solid #fff',
            zIndex: 2,
          }}
        />
        {splitPositionPct !== null ? (
          <Box
            data-testid="comparison-split-marker"
            sx={{
              position: 'absolute',
              left: `calc(8px + (100% - 16px) * ${splitPositionPct / 100})`,
              top: 8,
              bottom: 8,
              transform: 'translateX(-50%)',
              borderLeft: '2px dashed',
              borderColor: '#64748B',
              zIndex: 3,
            }}
          />
        ) : null}
        {baselinePlacement ? (
          <Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
            sx={{
              position: 'absolute',
              left: `calc(8px + (100% - 16px) * ${baselinePlacement.leftPct / 100})`,
              top: 0,
              maxWidth: `calc((100% - 16px) * ${Math.max(baselinePlacement.widthPct, 18) / 100})`,
              zIndex: 4,
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 700, color: VISUAL_TONES.orange.accent, whiteSpace: 'nowrap' }}>
              Baseline window
            </Typography>
            <Chip
              size="small"
              label={formatDurationCompact(durationBetween(props.baselineStart, props.baselineEnd))}
              sx={{
                height: 22,
                borderColor: VISUAL_TONES.orange.border,
                bgcolor: '#fff7ed',
                color: VISUAL_TONES.orange.accent,
                fontWeight: 700,
              }}
              variant="outlined"
            />
          </Stack>
        ) : null}
        {followupPlacement ? (
          <Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
            justifyContent="flex-end"
            sx={{
              position: 'absolute',
              left: `calc(8px + (100% - 16px) * ${followupPlacement.leftPct / 100})`,
              top: 0,
              width: `calc((100% - 16px) * ${Math.max(followupPlacement.widthPct, 18) / 100})`,
              zIndex: 4,
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 700, color: VISUAL_TONES.green.accent, whiteSpace: 'nowrap' }}>
              Follow-up window
            </Typography>
            <Chip
              size="small"
              label={formatDurationCompact(durationBetween(props.followupStart, props.followupEnd))}
              sx={{
                height: 22,
                borderColor: VISUAL_TONES.green.border,
                bgcolor: '#f0fdf4',
                color: VISUAL_TONES.green.accent,
                fontWeight: 700,
              }}
              variant="outlined"
            />
          </Stack>
        ) : null}
      </Box>

      {splitPositionPct !== null ? (
        <Box sx={{ textAlign: 'center', mt: 0.75 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: '#0f172a', display: 'block' }}>
            {formatCompactDateTime(pivotTimestamp)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {midpointTitle}
          </Typography>
        </Box>
      ) : null}

      <Stack direction="row" justifyContent="space-between" sx={{ mt: 1.25, px: 0.5 }}>
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a' }}>
            Start
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatCompactDateTime(trackStart)}
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a' }}>
            End
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatCompactDateTime(trackEnd)}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
}

function MeasurementNote(props: { message: string }) {
  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="flex-start"
      sx={{
        mt: 2,
        px: 1.25,
        py: 1.1,
        borderTop: '1px solid',
        borderColor: '#E5E7EB',
        color: 'text.secondary',
      }}
    >
      <InfoOutlined aria-hidden sx={{ fontSize: 18, mt: '2px', color: VISUAL_TONES.blue.accent }} />
      <Typography variant="body2">
        <Box component="span" sx={{ fontWeight: 600, color: '#0f172a' }}>
          Measurement note:
        </Box>{' '}
        {props.message}
      </Typography>
    </Stack>
  );
}

function ReportWindowsCard(props: {
  visual?: ReportVisual | null;
  reportScopeStart?: string | null;
  reportScopeEnd?: string | null;
  baselineWindowStart?: string | null;
  baselineWindowEnd?: string | null;
  followupWindowStart?: string | null;
  followupWindowEnd?: string | null;
  areaLabel?: string | null;
  comparisonMethod?: string;
  syntheticPivot?: boolean;
  readOnly?: boolean;
  timeline?: CorrectiveActionImpactTimeline | null;
  partialStateMessages?: string[];
  demoIllustrationLabel?: string | null;
}) {
  const items = visualItems(props.visual);
  const itemById = new Map(items.map((item) => [String(item?.id || ''), item]));
  const scopeItem = itemById.get('report_scope') || null;
  const baselineItem = itemById.get('baseline_window') || null;
  const followupItem = itemById.get('followup_window') || null;
  const scopeRange = splitWindowRange(String(scopeItem?.ts || ''));
  const baselineRange = splitWindowRange(String(baselineItem?.ts || ''));
  const followupRange = splitWindowRange(String(followupItem?.ts || ''));
  const cleanAreaLabel = firstNonEmpty(
    formatZoneDisplayLabel(extractAreaDetail(String(scopeItem?.detail || ''))),
    formatZoneDisplayLabel(normalizeOptionalValue(props.areaLabel)),
    'Not specified',
  );
  const scopeStart = firstNonEmpty(props.reportScopeStart, scopeRange.start);
  const scopeEnd = firstNonEmpty(props.reportScopeEnd, scopeRange.end);
  const baselineStart = firstNonEmpty(props.baselineWindowStart, baselineRange.start);
  const baselineEnd = firstNonEmpty(props.baselineWindowEnd, baselineRange.end);
  const followupStart = firstNonEmpty(props.followupWindowStart, followupRange.start);
  const followupEnd = firstNonEmpty(props.followupWindowEnd, followupRange.end);
  const scopeDuration = durationBetween(scopeStart, scopeEnd);
  const baselineDuration = durationBetween(baselineStart, baselineEnd);
  const followupDuration = durationBetween(followupStart, followupEnd);
  const comparisonTrackStart = comparisonWindowTrackStart(baselineStart, followupStart);
  const comparisonTrackEnd = comparisonWindowTrackEnd(baselineEnd, followupEnd);
  const scopeDiffersFromComparison = windowSpanDiffersFromComparison(
    scopeStart,
    scopeEnd,
    comparisonTrackStart,
    comparisonTrackEnd,
  );
  const durationsComparable = baselineDuration !== null && followupDuration !== null;
  const durationsDiffer = durationsComparable
    && durationsDifferBeyondTolerance(baselineDuration, followupDuration);
  const matchedMidpointComparison = scopeDiffersFromComparison
    && durationsComparable
    && durationsMatchWithinTolerance(baselineDuration, followupDuration);
  const effectiveSyntheticPivot = Boolean(props.syntheticPivot || matchedMidpointComparison);
  const measurementMessage = durationsComparable
    ? effectiveSyntheticPivot
      ? durationsDiffer
        ? 'The approved report scope is wider than the comparison windows, so normalized exposure rates are used for the matched midpoint comparison.'
        : 'The baseline and follow-up windows are matched midpoint slices taken from inside the wider approved report scope.'
      : durationsDiffer
      ? 'Baseline and follow-up windows have different durations, so normalized exposure rates are used for comparison.'
      : 'Baseline and follow-up windows use the same duration.'
    : '';
  const hasAnyWindows = [scopeStart, scopeEnd, baselineStart, baselineEnd, followupStart, followupEnd].some(
    (value) => Boolean(String(value || '').trim()),
  );
  const timelineTrackStart = scopeDiffersFromComparison ? comparisonTrackStart : firstNonEmpty(comparisonTrackStart, scopeStart);
  const timelineTrackEnd = scopeDiffersFromComparison ? comparisonTrackEnd : firstNonEmpty(comparisonTrackEnd, scopeEnd);
  const timelineTrackDuration = durationBetween(timelineTrackStart, timelineTrackEnd);
  const comparisonGeometryReady = timelineTrackDuration !== null && timelineTrackDuration > 0;
  const scopeGeometryReady = scopeDuration !== null && scopeDuration > 0;
  const pivotChipLabel = effectiveSyntheticPivot
    ? 'Corrective-action midpoint (50% split)'
    : (props.timeline?.corrective_action.label || 'Corrective-action midpoint');
  const childWindowExplanation = effectiveSyntheticPivot
    ? `The baseline and follow-up windows are matched ${describeDurationSlice(baselineDuration)} slices taken from inside the wider ${formatDurationCompact(scopeDuration)} approved report scope.`
    : measurementMessage;

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: '#E5E7EB',
        borderRadius: 3,
        bgcolor: '#fff',
        boxShadow: '0 8px 24px rgba(15, 23, 42, 0.04)',
        p: { xs: 2, md: 3 },
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start" justifyContent="space-between" sx={{ mb: 2 }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ minWidth: 0 }}>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: '50%',
              bgcolor: VISUAL_TONES.blue.soft,
              color: VISUAL_TONES.blue.accent,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: '0 0 auto',
            }}
          >
            <EventNoteOutlined aria-hidden />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography component="h2" variant="h5" sx={{ fontSize: '1.6rem', fontWeight: 700, color: '#0f172a' }}>
              Measurement windows
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {effectiveSyntheticPivot
                ? 'These windows separate the full Signed Safety Scan scope from the shorter matched midpoint comparison windows inside it.'
                : 'These windows explain what the Signed Safety Scan covers and how baseline and follow-up comparison was framed.'}
            </Typography>
          </Box>
        </Stack>
        {props.readOnly ? <Chip icon={<LockOutlined />} label="Read-only" size="small" variant="outlined" /> : null}
      </Stack>

      {(props.partialStateMessages || []).map((message) => (
        <Alert key={message} severity="info" icon={<InfoOutlined />} sx={{ mb: 1.5 }}>
          {message}
        </Alert>
      ))}
      {props.demoIllustrationLabel ? (
        <Alert severity="info" icon={<InfoOutlined />} sx={{ mb: 1.5 }}>
          {props.demoIllustrationLabel}
        </Alert>
      ) : null}

      {hasAnyWindows ? (
        <>
          {comparisonGeometryReady || scopeGeometryReady ? (
            <Box
              sx={{
                border: '1px solid',
                borderColor: '#E5E7EB',
                borderRadius: 3,
                bgcolor: '#F8FAFC',
                p: { xs: 2, md: 2.5 },
              }}
            >
              {scopeGeometryReady ? (
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: VISUAL_TONES.blue.border,
                    borderRadius: 3,
                    bgcolor: '#F8FBFF',
                    px: { xs: 1.5, md: 2 },
                    py: { xs: 1.5, md: 1.75 },
                    mb: scopeDiffersFromComparison && comparisonGeometryReady ? 2 : 0,
                  }}
                >
                  <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: '50%',
                        border: '1px solid',
                        borderColor: VISUAL_TONES.blue.border,
                        bgcolor: VISUAL_TONES.blue.soft,
                        color: VISUAL_TONES.blue.accent,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flex: '0 0 auto',
                      }}
                    >
                      <TrackChangesOutlined aria-hidden sx={{ fontSize: 22 }} />
                    </Box>
                    <Typography component="h3" variant="h6" sx={{ fontWeight: 700, color: VISUAL_TONES.blue.accent }}>
                      Report scope
                    </Typography>
                    <Chip
                      size="small"
                      label={formatDurationCompact(scopeDuration)}
                      variant="outlined"
                      sx={{
                        borderColor: VISUAL_TONES.blue.border,
                        bgcolor: 'rgba(255,255,255,0.9)',
                        color: VISUAL_TONES.blue.accent,
                        fontWeight: 700,
                      }}
                    />
                    <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
                    <Box
                      aria-label="Report scope date range"
                      sx={{
                        px: 1.25,
                        py: 0.9,
                        borderRadius: 1.75,
                        border: '1px solid',
                        borderColor: '#CBD5E1',
                        bgcolor: 'rgba(255,255,255,0.95)',
                        color: '#0f172a',
                        fontSize: '0.86rem',
                        lineHeight: 1.4,
                        wordBreak: 'break-word',
                      }}
                    >
                      {formatCompactWindowRange(scopeStart, scopeEnd)}
                    </Box>
                  </Stack>

                  <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 1.25, color: 'text.secondary' }}>
                    <PlaceOutlined aria-hidden sx={{ fontSize: 18, color: VISUAL_TONES.blue.accent }} />
                    <Typography variant="body2">{`Area: ${cleanAreaLabel}`}</Typography>
                  </Stack>

                  {!scopeDiffersFromComparison && comparisonGeometryReady ? (
                    <UnifiedComparisonTimeline
                      trackStart={timelineTrackStart}
                      trackEnd={timelineTrackEnd}
                      baselineStart={baselineStart}
                      baselineEnd={baselineEnd}
                      followupStart={followupStart}
                      followupEnd={followupEnd}
                      pivotLabel={props.timeline?.corrective_action.label}
                      syntheticPivot={effectiveSyntheticPivot}
                    />
                  ) : null}
                </Box>
              ) : null}

              {scopeDiffersFromComparison && comparisonGeometryReady ? (
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: '#E5E7EB',
                    borderRadius: 3,
                    bgcolor: '#fff',
                    px: { xs: 1.5, md: 2 },
                    py: { xs: 1.5, md: 1.75 },
                  }}
                >
                  <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography component="h3" variant="h6" sx={{ fontWeight: 700, color: '#0f172a' }}>
                      Matched comparison windows
                    </Typography>
                    <Chip
                      size="small"
                      label={formatDurationCompact(timelineTrackDuration)}
                      variant="outlined"
                      sx={{ fontWeight: 700 }}
                    />
                    <Box
                      aria-label="Matched comparison window date range"
                      sx={{
                        px: 1.25,
                        py: 0.9,
                        borderRadius: 1.75,
                        border: '1px solid',
                        borderColor: '#CBD5E1',
                        bgcolor: '#F8FAFC',
                        color: '#0f172a',
                        fontSize: '0.86rem',
                        lineHeight: 1.4,
                        wordBreak: 'break-word',
                      }}
                    >
                      {formatCompactWindowRange(timelineTrackStart, timelineTrackEnd)}
                    </Box>
                  </Stack>

                  <UnifiedComparisonTimeline
                    trackStart={timelineTrackStart}
                    trackEnd={timelineTrackEnd}
                    baselineStart={baselineStart}
                    baselineEnd={baselineEnd}
                    followupStart={followupStart}
                    followupEnd={followupEnd}
                    pivotLabel={props.timeline?.corrective_action.label}
                    syntheticPivot={effectiveSyntheticPivot}
                  />
                </Box>
              ) : null}

              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
                <Chip
                  size="small"
                  label="Baseline window"
                  sx={{
                    borderColor: VISUAL_TONES.orange.border,
                    bgcolor: VISUAL_TONES.orange.soft,
                    color: VISUAL_TONES.orange.accent,
                    fontWeight: 700,
                  }}
                  variant="outlined"
                />
                <Chip
                  size="small"
                  label="Follow-up window"
                  data-testid="followup-window-chip"
                  data-followup-tone={props.timeline?.followup_tone || 'neutral'}
                  sx={{
                    borderColor: VISUAL_TONES.green.border,
                    bgcolor: VISUAL_TONES.green.soft,
                    color: VISUAL_TONES.green.accent,
                    fontWeight: 700,
                  }}
                  variant="outlined"
                />
                <Chip
                  size="small"
                  label={pivotChipLabel}
                  sx={{
                    borderColor: '#BFDBFE',
                    bgcolor: '#EFF6FF',
                    color: VISUAL_TONES.blue.accent,
                    fontWeight: 700,
                  }}
                  variant="outlined"
                />
              </Stack>

              {props.timeline ? <ObservationCoverageBand timeline={props.timeline} /> : null}

              <Box sx={{ mt: 1.5 }}>
                <WindowDetailRow
                  title="Baseline window"
                  start={baselineStart}
                  end={baselineEnd}
                  durationMs={baselineDuration}
                  color="orange"
                />
                <WindowDetailRow
                  title="Follow-up window"
                  start={followupStart}
                  end={followupEnd}
                  durationMs={followupDuration}
                  color="green"
                />
              </Box>

              {childWindowExplanation ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                  {childWindowExplanation}
                </Typography>
              ) : null}
            </Box>
          ) : (
            <Stack spacing={1.5}>
              <Box
                aria-label="Report scope date range"
                sx={{ px: 1.25, py: 1, borderRadius: 2, border: '1px solid', borderColor: '#CBD5E1', bgcolor: '#F8FAFC' }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: VISUAL_TONES.blue.accent }}>
                  Report scope
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {formatCompactWindowRange(scopeStart, scopeEnd)}
                </Typography>
              </Box>
              <Box
                aria-label="Baseline window date range"
                sx={{ px: 1.25, py: 1, borderRadius: 2, border: '1px solid', borderColor: VISUAL_TONES.orange.border, bgcolor: VISUAL_TONES.orange.soft }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: VISUAL_TONES.orange.accent }}>
                  Baseline window
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {formatCompactWindowRange(baselineStart, baselineEnd)}
                </Typography>
              </Box>
              <Box
                aria-label="Follow-up window date range"
                sx={{ px: 1.25, py: 1, borderRadius: 2, border: '1px solid', borderColor: VISUAL_TONES.green.border, bgcolor: VISUAL_TONES.green.soft }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: VISUAL_TONES.green.accent }}>
                  Follow-up window
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {formatCompactWindowRange(followupStart, followupEnd)}
                </Typography>
              </Box>
              {childWindowExplanation ? (
                <Typography variant="body2" color="text.secondary">
                  {childWindowExplanation}
                </Typography>
              ) : null}
            </Stack>
          )}

          {measurementMessage && !childWindowExplanation ? <MeasurementNote message={measurementMessage} /> : null}
        </>
      ) : (
        <Alert severity="info">No Signed Safety Scan windows were returned for this artifact.</Alert>
      )}
    </Box>
  );
}

function AnalyticsSummaryCard(props: {
  visual?: ReportVisual | null;
  tone?: VisualTone;
  mode?: 'distribution' | 'bars';
}) {
  const items: Record<string, any>[] = visualItems(props.visual).map((item) => ({
    ...(item as Record<string, any>),
    label: formatZoneDisplayLabel(normalizeHourPeriodLabel(String((item as Record<string, any>)?.label || ''))),
  }));
  const tone = VISUAL_TONES[props.tone || 'neutral'];
  const total = items.reduce((sum, item) => sum + (typeof item?.value === 'number' ? item.value : 0), 0);
  const topItem = items.reduce<Record<string, any> | null>((currentTop, item) => {
    if (!currentTop) return item;
    return (item?.value || 0) > (currentTop?.value || 0) ? item : currentTop;
  }, null);
  const percentage = topItem && total > 0 ? (Number(topItem.value || 0) / total) * 100 : 0;
  const nonZeroItems = items.filter((item) => Number(item?.value || 0) > 0);
  const singleCategory = nonZeroItems.length === 1;
  // For hour-bucket bar charts, trim leading/trailing all-zero hours so labels
  // don't crowd the axis with empty buckets (keep one zero bucket of padding).
  const barItems = (() => {
    if (items.length < 12) return items;
    const firstNonZero = items.findIndex((item) => Number(item?.value || 0) > 0);
    if (firstNonZero === -1) return items;
    let lastNonZero = items.length - 1;
    while (lastNonZero > 0 && Number(items[lastNonZero]?.value || 0) === 0) lastNonZero -= 1;
    const start = Math.max(0, firstNonZero - 1);
    const end = Math.min(items.length - 1, lastNonZero + 1);
    return items.slice(start, end + 1);
  })();

  return (
    <SectionCard title={props.visual?.title || 'Analytics'} subtitle={props.visual?.insight}>
      {items.length === 0 ? (
        <Alert severity="info">No signed-off analytics were returned for this card.</Alert>
      ) : props.mode === 'bars' ? (
        <Stack spacing={2}>
          <Box sx={{ width: '100%', height: 160 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={barItems.map((item) => ({ label: remapSafetyScanCustomerLabel(item.label || ''), value: Number(item?.value || 0) }))}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="label" tickLine={false} axisLine={false} minTickGap={18} interval="preserveStartEnd" fontSize={10} />
                <YAxis hide />
                <RechartsTooltip
                  formatter={(value: number) => formatNumericValue(value, 0)}
                  allowEscapeViewBox={{ x: false, y: false }}
                  wrapperStyle={{ outline: 'none' }}
                />
                <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                  {barItems.map((item, index) => (
                    <Cell
                      key={`${item.label || 'bar'}-${index}`}
                      fill={index === (barItems.findIndex((entry) => entry === topItem) ?? -1) ? tone.accent : `${tone.accent}66`}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Box>
          <Box
            sx={{
              border: '1px solid',
              borderColor: tone.border,
              borderRadius: 2,
              bgcolor: tone.soft,
              px: 1.5,
              py: 1.25,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {props.visual?.title === 'Hourly activity in scan window' ? 'Top hour' : 'Top period'}
            </Typography>
            <Typography variant="subtitle1" sx={{ mt: 0.4, fontWeight: 700 }}>
              {String(topItem?.label || 'Not available')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {formatNumericValue(Number(topItem?.value || 0), 0)} incidents
            </Typography>
          </Box>
        </Stack>
      ) : singleCategory ? (
        <Box
          sx={{
            border: '1px solid',
            borderColor: tone.border,
            borderRadius: 2,
            bgcolor: tone.soft,
            px: 1.5,
            py: 1.25,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            {props.visual?.title === 'Event breakdown' ? 'Top event category' : 'Top hotspot'}
          </Typography>
          <Typography variant="subtitle1" sx={{ mt: 0.4, fontWeight: 700 }}>
            {String(topItem?.label || 'Not available')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {`All ${formatNumericValue(total, 0)} signed-off events fall in this ${props.visual?.title === 'Event breakdown' ? 'category' : 'hotspot'}, so no distribution comparison is available.`}
          </Typography>
        </Box>
      ) : (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }}>
          <Box sx={{ width: 116, height: 116, flex: '0 0 auto' }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={items.slice(0, 4).map((item) => ({ name: remapSafetyScanCustomerLabel(item.label || ''), value: Number(item?.value || 0) }))}
                  dataKey="value"
                  cx="50%"
                  cy="50%"
                  innerRadius={34}
                  outerRadius={52}
                  paddingAngle={2}
                  stroke="none"
                >
                  {items.slice(0, 4).map((item, index) => (
                    <Cell
                      key={`${item.label || 'slice'}-${index}`}
                      fill={index === 0 ? tone.accent : `${tone.accent}${['AA', '88', '66', '44'][index] || '33'}`}
                    />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <Box sx={{ mt: -8, textAlign: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                {formatNumericValue(percentage, 0)}%
              </Typography>
            </Box>
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary">
              {props.visual?.title === 'Event breakdown' ? 'Top event category' : 'Top hotspot'}
            </Typography>
            <Typography variant="subtitle1" sx={{ mt: 0.5, fontWeight: 700 }}>
              {String(topItem?.label || 'Not available')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
              {formatNumericValue(Number(topItem?.value || 0), 0)} of {formatNumericValue(total, 0)} total
            </Typography>
            <Stack spacing={1} sx={{ mt: 1.5 }}>
              {items.slice(0, 3).map((item, index) => {
                const itemPercent = total > 0 ? (Number(item?.value || 0) / total) * 100 : 0;
                return (
                  <Box key={`${item.label || 'item'}-${index}`}>
                    <Stack direction="row" justifyContent="space-between" spacing={1} sx={{ mb: 0.5 }}>
                      <Typography variant="body2" sx={{ minWidth: 0 }}>
                        {remapSafetyScanCustomerLabel(String(item.label || '').trim()) || `Item ${index + 1}`}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formatNumericValue(Number(item?.value || 0), 0)}
                      </Typography>
                    </Stack>
                    <Box sx={{ height: 8, borderRadius: 999, bgcolor: '#e2e8f0', overflow: 'hidden' }}>
                      <Box sx={{ width: `${itemPercent}%`, height: '100%', bgcolor: tone.accent }} />
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          </Box>
        </Stack>
      )}
    </SectionCard>
  );
}

function EvidenceGalleryCard(props: {
  item: EvidenceGalleryItem;
  index: number;
  evidenceNote: string;
}) {
  const itemId = String(props.item?.id || `${props.item?.incident_id || 'incident'}-${props.index}`);
  const kind = String(props.item?.kind || '').trim().toLowerCase() === 'clip' ? 'clip' : 'snapshot';
  const mediaUri = normalizeOptionalValue(props.item?.media_uri);
  const thumbnailUri = normalizeOptionalValue(props.item?.thumbnail_uri) || mediaUri;
  const thumbnailLooksSeparate = Boolean(thumbnailUri && mediaUri && thumbnailUri !== mediaUri);
  const renderPreviewAsVideo = kind === 'clip' && !thumbnailLooksSeparate;
  const [previewUrl, setPreviewUrl] = useState('');
  const [resolvedMediaUrl, setResolvedMediaUrl] = useState('');
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const objectUrls: string[] = [];

    async function fetchBlobUrl(uri: string): Promise<string> {
      const response = await api.get(uri, { responseType: 'blob' });
      const nextUrl = URL.createObjectURL(response.data as Blob);
      objectUrls.push(nextUrl);
      return nextUrl;
    }

    async function loadMedia() {
      if (!thumbnailUri && !mediaUri) {
        setPreviewUrl('');
        setResolvedMediaUrl('');
        setLoadFailed(true);
        return;
      }
      setLoadFailed(false);
      try {
        const previewPromise = thumbnailUri
          ? fetchBlobUrl(thumbnailUri)
          : Promise.resolve('');
        const mediaPromise = mediaUri
          ? (
              mediaUri === thumbnailUri
                ? previewPromise
                : fetchBlobUrl(mediaUri)
            )
          : Promise.resolve('');
        const [nextPreviewUrl, nextMediaUrl] = await Promise.all([previewPromise, mediaPromise]);
        if (cancelled) return;
        setPreviewUrl(nextPreviewUrl || nextMediaUrl || '');
        setResolvedMediaUrl(nextMediaUrl || nextPreviewUrl || '');
      } catch {
        if (cancelled) return;
        setPreviewUrl('');
        setResolvedMediaUrl('');
        setLoadFailed(true);
      }
    }

    void loadMedia();

    return () => {
      cancelled = true;
      objectUrls.forEach((url) => {
        try {
          URL.revokeObjectURL(url);
        } catch {
          // ignore blob cleanup failures
        }
      });
    };
  }, [mediaUri, thumbnailUri]);

  const visiblePreviewUrl = previewUrl || resolvedMediaUrl;
  const openUrl = resolvedMediaUrl || previewUrl;

  return (
    <Box
      key={itemId}
      sx={{
        flex: '0 0 240px',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2.5,
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      <Box sx={{ position: 'relative', aspectRatio: '16 / 9', bgcolor: '#0f172a' }}>
        {!loadFailed && visiblePreviewUrl ? (
          renderPreviewAsVideo ? (
            <Box
              component="video"
              src={visiblePreviewUrl}
              muted
              playsInline
              preload="metadata"
              sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          ) : (
            <Box
              component="img"
              src={visiblePreviewUrl}
              alt="Representative evidence snapshot"
              sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          )
        ) : (
          <Stack
            spacing={1}
            alignItems="center"
            justifyContent="center"
            sx={{ height: '100%', color: 'rgba(255,255,255,0.8)', px: 2, textAlign: 'center' }}
          >
            <Typography variant="subtitle2">{kind === 'clip' ? 'Clip unavailable' : 'Snapshot unavailable'}</Typography>
            <Typography variant="caption">
              This signed-off media reference is missing or could not be loaded.
            </Typography>
          </Stack>
        )}
        <Chip
          label={kind === 'clip' ? 'Clip' : 'Snapshot'}
          size="small"
          sx={{
            position: 'absolute',
            top: 10,
            left: 10,
            bgcolor: kind === 'clip' ? VISUAL_TONES.purple.soft : VISUAL_TONES.green.soft,
          }}
        />
      </Box>
      <Box sx={{ p: 1.5 }}>
        <Typography variant="caption" color="text.secondary">
          {formatDateTime(props.item?.captured_at)}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5, color: '#0f172a' }}>
          {props.evidenceNote}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          {`${kind === 'clip' ? 'Clip' : 'Snapshot'} · ${formatZoneDisplayLabel(
            firstNonEmpty(normalizeOptionalValue(props.item?.area_label), normalizeOptionalValue(props.item?.rule_label), 'Signed-off evidence sample'),
          )} · ${formatTokenLabel(props.item?.status)}`}
        </Typography>
        {openUrl && !loadFailed ? (
          <Button variant="outlined" size="small" href={openUrl} target="_blank" rel="noreferrer">
            {kind === 'clip' ? 'Open clip' : 'Open snapshot'}
          </Button>
        ) : (
          <Chip label="Unavailable" size="small" variant="outlined" />
        )}
      </Box>
    </Box>
  );
}

function EvidenceGallery(props: {
  gallery?: EvidenceGalleryPayload | null;
  evidenceSummary: EvidenceSummary;
  evidenceNote: string;
}) {
  const mediaItems = Array.isArray(props.gallery?.items) ? props.gallery?.items || [] : [];
  const representativeCopy = typeof props.evidenceSummary.representative_items === 'number'
    ? `Representative evidence retained: ${formatNumericValue(props.evidenceSummary.representative_items, 0)} archived item${props.evidenceSummary.representative_items === 1 ? '' : 's'} in the signed-off package.`
    : 'Representative item count was not included in the signed-off package.';

  return (
    <SectionCard
      title="Representative evidence"
      subtitle="This archived evidence sample is anchored to the finalized report and remains read-only even when newer workflow activity exists."
    >
      <InfoNote message={representativeCopy} />
      {mediaItems.length === 0 ? (
        <Alert severity="info">No representative snapshot or clip is currently available for this Signed Safety Scan.</Alert>
      ) : (
        <Stack direction="row" spacing={1.5} sx={{ overflowX: 'auto', pb: 0.5 }}>
          {mediaItems.map((item, index) => (
            <EvidenceGalleryCard
              key={String(item?.id || `${item?.incident_id || 'incident'}-${index}`)}
              item={item}
              index={index}
              evidenceNote={props.evidenceNote}
            />
          ))}
        </Stack>
      )}
    </SectionCard>
  );
}

const SafetyReportArtifactDetail: React.FC = () => {
  const navigate = useNavigate();
  const { reportId: routeReportId } = useParams<{ reportId: string }>();
  const [searchParams] = useSearchParams();
  const { effectiveUser: user } = useAuth();
  const canConfigureScheduledScans = useMemo(
    () => getCapabilitiesFromUser(user).includes('scan:configure'),
    [user],
  );
  const canDownloadInternal = canDownloadInternalPackage(user);
  const canDownloadCustomer = canDownloadCustomerPackage(user);
  const canViewAppendix = canViewTechnicalAppendix(user);
  const canDownloadExternalSafe = canDownloadExternalSafePackage(user);
  const canDownloadExternalSafePdf = canDownloadExternalSafeReportPdf(user);
  const canShowExportFormats = canDownloadReportExportFormats(user);
  const canDownloadDocxCsv = canDownloadReportArtifactDocxOrCsv(user);
  const [openData, setOpenData] = useState<SafetyReportOpenResponse | null>(null);
  const [fullArtifactData, setFullArtifactData] = useState<SafetyReportArtifactDetailResponse | null>(null);
  const [bootstrapLoading, setBootstrapLoading] = useState(true);
  const [artifactLoading, setArtifactLoading] = useState(true);
  const [bootstrapError, setBootstrapError] = useState<string | null>(null);
  const [artifactError, setArtifactError] = useState<string | null>(null);
  const [liveWorkflowClaimReadiness, setLiveWorkflowClaimReadiness] = useState<WorkflowClaimReadinessResponse | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [downloadLoading, setDownloadLoading] = useState<'zip' | 'pdf' | 'pdf_detailed' | 'docx' | 'csv' | null>(null);
  const [linkedSafetyCase, setLinkedSafetyCase] = useState<SafetyCaseRecord | null>(null);

  const reportId = String(routeReportId || '').trim();
  const bootstrapArtifactData = useMemo(
    () => buildBootstrapArtifactDetail(openData, reportId),
    [openData, reportId],
  );
  const artifactData = useMemo(
    () => {
      const selectedFullArtifactData = hasRenderableArtifactPayload(fullArtifactData)
        ? fullArtifactData
        : null;
      return selectedFullArtifactData ?? bootstrapArtifactData;
    },
    [bootstrapArtifactData, fullArtifactData],
  );
  const hasFullArtifact = hasRenderableArtifactPayload(artifactData);

  useEffect(() => {
    let cancelled = false;
    if (!reportId) {
      setBootstrapError('Missing report id.');
      setBootstrapLoading(false);
      setArtifactLoading(false);
      return () => {
        cancelled = true;
      };
    }

    setOpenData(null);
    setFullArtifactData(null);
    setBootstrapLoading(true);
    setArtifactLoading(true);
    setBootstrapError(null);
    setArtifactError(null);

    void reportsApi.openSafetyReport(reportId)
      .then((response) => {
        if (cancelled) return;
        if (!response?.ok) {
          setBootstrapError(response?.error || 'Unable to open this signed Safety Scan.');
          return;
        }
        setOpenData(response);
      })
      .catch((err: any) => {
        if (cancelled) return;
        setBootstrapError(err?.message || 'Unable to open this signed Safety Scan.');
      })
      .finally(() => {
        if (!cancelled) setBootstrapLoading(false);
      });

    void reportsApi.getSafetyReportArtifact(reportId)
      .then((response) => {
        if (cancelled) return;
        if (hasRenderableArtifactPayload(response)) {
          setFullArtifactData(response);
          return;
        }
        const responseRecord = (response && typeof response === 'object')
          ? (response as Record<string, any>)
          : {};
        setArtifactError(
          String(responseRecord.error || responseRecord.detail || '').trim()
          || 'Unable to load the full signed Safety Scan artifact.',
        );
      })
      .catch((err: any) => {
        if (cancelled) return;
        setArtifactError(err?.message || 'Unable to load the full signed Safety Scan artifact.');
      })
      .finally(() => {
        if (!cancelled) setArtifactLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [reportId]);

  useEffect(() => {
    const findingId = String(artifactData?.artifact?.finding_id || '').trim();
    if (!findingId) {
      setLinkedSafetyCase(null);
      return;
    }
    let cancelled = false;
    reportsApi.openSafetyCase(findingId)
      .then((response) => {
        if (!cancelled) setLinkedSafetyCase(response.safety_case || response.finding || null);
      })
      .catch(() => {
        if (!cancelled) setLinkedSafetyCase(null);
      });
    return () => {
      cancelled = true;
    };
  }, [artifactData?.artifact?.finding_id]);

  const artifact = artifactData?.artifact || null;
  const persistedReport = artifactData?.persisted_report || null;
  const workplaceConditionFields = useMemo(
    () => resolveWorkplaceConditionFromArtifact(persistedReport),
    [persistedReport],
  );
  const reportProjection = artifactData?.report_projection || null;
  const technicalDetails = artifactData?.technical_details || null;
  const outcomeContext = useMemo(
    () => resolveSafetyReportOutcomeContext({
      reportProjection,
      technicalDetails: technicalDetails as { source_context?: Record<string, unknown> | null } | null,
    }),
    [reportProjection, technicalDetails],
  );
  const exportManifest = artifactData?.export_manifest || null;
  const customerEvidenceSummary = ((technicalDetails as Record<string, any> | null)?.customer_evidence_summary || null) as Record<string, any> | null;
  const decisionReadiness = artifactData?.decision_readiness || technicalDetails?.decision_readiness || null;
  const comparativeIntelligence = artifactData?.comparative_safety_intelligence
    || (technicalDetails as Record<string, any> | null)?.comparative_safety_intelligence
    || null;
  const ehsReviewWorksheet = artifactData?.ehs_review_worksheet || null;
  const safetyScanBuyerReport = artifactData?.safety_scan_buyer_report || null;
  const safetyScanReportV2 = artifactData?.safety_scan_report_v2
    || (technicalDetails as Record<string, unknown> | null)?.safety_scan_report_v2
    || null;
  const liveFollowup = artifactData?.live_followup || null;
  const buyerExecutiveFinding = safetyScanBuyerReport?.executive_summary?.safety_finding || null;
  const scope = artifact?.scope || null;
  const timeWindow = artifact?.time_window || null;
  const compactBuyerFace = useMemo(
    () => usesCompactBuyerFace({
      riskType: scope?.risk_type as string | undefined,
      persistedReport: persistedReport as Record<string, unknown> | null,
      artifactScope: scope as Record<string, unknown> | null,
      policyReportContext: safetyScanBuyerReport?.policy_report_context as Record<string, unknown> | null,
    }),
    [persistedReport, safetyScanBuyerReport?.policy_report_context, scope],
  );
  const artifactDisplayLabels = artifact?.display_labels || null;
  const artifactClaimsSummary = artifact?.claims_summary || null;
  const packageDisplay = exportManifest?.package_display || null;
  const projectionCustomerSummary = reportProjection?.customer_summary || null;
  const technicalCustomerSummary = technicalDetails?.customer_summary || null;
  const frozenArtifactClaimBoundary = useMemo(
    () => resolveFrozenArtifactClaimBoundary(reportProjection),
    [reportProjection],
  );
  const artifactWorkflowId = useMemo(
    () => firstNonEmpty(artifact?.workflow_id, reportProjection?.workflow_state?.workflow_id, persistedReport?.workflow_id),
    [artifact?.workflow_id, persistedReport?.workflow_id, reportProjection?.workflow_state?.workflow_id],
  );
  useEffect(() => {
    setLiveWorkflowClaimReadiness(null);
    if (String(frozenArtifactClaimBoundary?.wording || '').trim() || !artifactWorkflowId || !reportId) {
      return undefined;
    }
    let cancelled = false;
    void reportsApi.getWorkflowClaimReadiness(artifactWorkflowId, reportId)
      .then((response) => {
        if (!cancelled && response?.ok) setLiveWorkflowClaimReadiness(response);
      })
      .catch(() => {
        if (!cancelled) setLiveWorkflowClaimReadiness(null);
      });
    return () => {
      cancelled = true;
    };
  }, [artifactWorkflowId, frozenArtifactClaimBoundary?.wording, reportId]);
  const claimBoundaryPresentation = useMemo(
    () => resolveArtifactClaimBoundaryPresentation({
      frozenBoundary: frozenArtifactClaimBoundary,
      liveClaimReadiness: liveWorkflowClaimReadiness,
      legacyClaimBoundaryText: technicalCustomerSummary?.claim_boundary_text,
      legacyEffectivenessClaimBoundary: decisionReadiness?.effectiveness_verification?.claim_boundary,
    }),
    [
      decisionReadiness?.effectiveness_verification?.claim_boundary,
      frozenArtifactClaimBoundary,
      liveWorkflowClaimReadiness,
      technicalCustomerSummary?.claim_boundary_text,
    ],
  );
  const impactSummary = reportProjection?.workflow_state?.impact_summary || null;
  const evidenceSummary = useMemo(
    () => buildEvidenceSummary(artifactData, persistedReport, customerEvidenceSummary, exportManifest, technicalDetails),
    [artifactData, customerEvidenceSummary, exportManifest, persistedReport, technicalDetails],
  );
  const rawPackageFiles = exportManifest?.files;
  const packageFiles = Array.isArray(rawPackageFiles) ? rawPackageFiles : [];
  const packageFileCount = (() => {
    const raw = packageDisplay?.file_count;
    const parsed = typeof raw === 'number' ? raw : Number.parseInt(String(raw ?? ''), 10);
    if (Number.isFinite(parsed) && parsed >= 0) return parsed;
    return packageFiles.length || null;
  })();
  const packageFilesLabel = packageFileCount !== null
    ? `${packageFileCount} ${packageFileCount === 1 ? 'file' : 'files'}`
    : 'Not available';
  const artifactIncidentId = firstNonEmpty(
    artifact?.representative_incident_id,
    searchParams.get('incident_id'),
    searchParams.get('case_id'),
  );
  const artifactScopeAreaLabel = normalizeOptionalValue(artifactDisplayLabels?.area_label) || 'Not specified';
  const artifactActivityTypeLabel = normalizeOptionalValue(artifactDisplayLabels?.activity_type_label) || 'Safety event';
  const artifactSiteLabel = normalizeOptionalValue(artifactDisplayLabels?.site_label) || 'Not specified';
  const reportVisuals = useMemo<ReportVisual[]>(
    () => (Array.isArray(artifactData?.report_visuals) ? artifactData?.report_visuals || [] : []),
    [artifactData?.report_visuals],
  );
  const evidenceGallery = artifactData?.evidence_gallery || technicalDetails?.evidence_gallery || null;
  const impactMeasurementGraph = artifactData?.impact_measurement_graph || technicalDetails?.impact_measurement_graph || null;
  const recommendationSnapshot = artifactData?.recommendation_snapshot
    || technicalDetails?.recommendation_snapshot
    || ((persistedReport?.report_content as Record<string, any> | undefined)?.recommendation_snapshot ?? null);
  const recommendationSnapshotItems: RecommendationSnapshotItem[] = Array.isArray(recommendationSnapshot?.recommendations)
    ? recommendationSnapshot?.recommendations || []
    : [];
  const impactMeasurementVisual = useMemo(
    () => reportVisuals.find((visual) => visual.id === 'artifact_impact_measurement') || null,
    [reportVisuals],
  );
  const reportWindowsVisual = useMemo(
    () => reportVisuals.find((visual) => visual.id === 'artifact_windows_timeline') || null,
    [reportVisuals],
  );
  const signedOffReportScopeWindow = useMemo(
    () => resolveReportScopeWindow(
      reportWindowsVisual,
      ((customerEvidenceSummary?.report_scope_window
        || (technicalDetails as Record<string, any> | null)?.report_scope_window
        || null) as { start?: string | null; end?: string | null } | null),
      timeWindow,
    ),
    [customerEvidenceSummary?.report_scope_window, reportWindowsVisual, technicalDetails, timeWindow],
  );
  const correctiveActionImpactTimeline = useMemo(
    () => buildCorrectiveActionImpactTimeline({
      artifactData,
      reportScopeWindow: signedOffReportScopeWindow,
      reportWindowsVisual,
      impactMeasurementWaived: String(impactSummary?.status || '').trim().toLowerCase() === 'waived'
        || String(impactSummary?.measurement_status || '').trim().toLowerCase() === 'waived',
    }),
    [artifactData, impactSummary?.measurement_status, impactSummary?.status, reportWindowsVisual, signedOffReportScopeWindow],
  );
  const impactComparisonNote = useMemo(() => {
    if (correctiveActionImpactTimeline.uses_synthetic_midpoint) {
      return 'The approved report scope can be wider than the comparison windows; baseline and follow-up use a matched 50% midpoint split carved from inside that scope.';
    }
    const baselineDuration = durationBetween(
      correctiveActionImpactTimeline.baseline_window.start,
      correctiveActionImpactTimeline.baseline_window.end,
    );
    const followupDuration = durationBetween(
      correctiveActionImpactTimeline.followup_window.start,
      correctiveActionImpactTimeline.followup_window.end,
    );
    if (baselineDuration === null || followupDuration === null) {
      return normalizeOptionalValue(impactMeasurementGraph?.comparability?.measurement_note) || null;
    }
    const durationsDiffer = durationsDifferBeyondTolerance(baselineDuration, followupDuration);
    const scopeStart = correctiveActionImpactTimeline.report_scope.start;
    const scopeEnd = correctiveActionImpactTimeline.report_scope.end;
    const comparisonTrackStart = comparisonWindowTrackStart(
      correctiveActionImpactTimeline.baseline_window.start,
      correctiveActionImpactTimeline.followup_window.start,
    );
    const comparisonTrackEnd = comparisonWindowTrackEnd(
      correctiveActionImpactTimeline.baseline_window.end,
      correctiveActionImpactTimeline.followup_window.end,
    );
    const scopeDiffersFromComparison = windowSpanDiffersFromComparison(
      scopeStart,
      scopeEnd,
      comparisonTrackStart,
      comparisonTrackEnd,
    );
    const matchedMidpointComparison = scopeDiffersFromComparison
      && durationsMatchWithinTolerance(baselineDuration, followupDuration);
    if (matchedMidpointComparison) {
      return 'The approved report scope can be wider than the comparison windows; baseline and follow-up use a matched midpoint split carved from inside that scope.';
    }
    const explicitNote = normalizeOptionalValue(impactMeasurementGraph?.comparability?.measurement_note);
    if (explicitNote && durationsDiffer) {
      return explicitNote;
    }
    return durationsDiffer
      ? 'Baseline and follow-up windows have different durations, so normalized exposure rates are used for comparison.'
      : 'Baseline and follow-up windows use the same duration.';
  }, [
    correctiveActionImpactTimeline.baseline_window.end,
    correctiveActionImpactTimeline.baseline_window.start,
    correctiveActionImpactTimeline.followup_window.end,
    correctiveActionImpactTimeline.followup_window.start,
    correctiveActionImpactTimeline.report_scope.end,
    correctiveActionImpactTimeline.report_scope.start,
    correctiveActionImpactTimeline.uses_synthetic_midpoint,
    impactMeasurementGraph?.comparability?.measurement_note,
  ]);
  const hotspotVisual = useMemo(
    () => reportVisuals.find((visual) => visual.id === 'artifact_hotspot_concentration')
      || reportVisuals.find((visual) => visual.title === 'Hotspot concentration')
      || null,
    [reportVisuals],
  );
  const timePatternVisual = useMemo(
    () => reportVisuals.find((visual) => visual.id === 'artifact_time_pattern')
      || reportVisuals.find((visual) => visual.title === 'Time-of-day pattern')
      || null,
    [reportVisuals],
  );
  const workplaceConditionContext = useMemo(
    () => buildWorkplaceConditionContextFromArtifact({
      persistedReport: persistedReport
        ? {
          ...persistedReport,
          technical_details: technicalDetails as Record<string, unknown> | null,
        }
        : null,
      artifactScope: scope,
      artifactTimeWindow: timeWindow,
      artifactAreaLabel: artifactDisplayLabels?.area_label || null,
      buyerReport: safetyScanBuyerReport,
      timePatternVisual,
    }),
    [
      artifactDisplayLabels?.area_label,
      persistedReport,
      safetyScanBuyerReport,
      scope,
      technicalDetails,
      timePatternVisual,
      timeWindow,
    ],
  );
  const eventBreakdownVisual = useMemo(
    () => reportVisuals.find((visual) => visual.id === 'artifact_event_breakdown')
      || reportVisuals.find((visual) => visual.title === 'Event breakdown')
      || null,
    [reportVisuals],
  );
  const [selectedImpactGranularity, setSelectedImpactGranularity] = useState<ArtifactGranularity>('day');

  useEffect(() => {
    const defaultGranularity = impactMeasurementGraph?.default_granularity;
    const hasHourlySeries = Array.isArray(impactMeasurementGraph?.series?.hour) && (impactMeasurementGraph?.series?.hour?.length || 0) > 0;
    setSelectedImpactGranularity(
      hasHourlySeries
        ? 'hour'
        : isArtifactGranularity(defaultGranularity)
        ? defaultGranularity
        : 'day',
    );
  }, [impactMeasurementGraph?.default_granularity, impactMeasurementGraph?.series?.hour]);

  const reportsBackPath = useMemo(() => {
    const query = new URLSearchParams();
    const orgId = String(searchParams.get('org_id') || '').trim();
    const siteId = firstNonEmpty(searchParams.get('site_id'), scope?.site_id);
    const reportFrom = firstNonEmpty(searchParams.get('report_from'), timeWindow?.start);
    const reportTo = firstNonEmpty(searchParams.get('report_to'), timeWindow?.end);
    if (orgId) query.set('org_id', orgId);
    if (siteId) query.set('site_id', siteId);
    if (reportFrom) query.set('report_from', reportFrom);
    if (reportTo) query.set('report_to', reportTo);
    const rendered = query.toString();
    return rendered ? `/acceptance/reports?${rendered}` : '/acceptance/reports';
  }, [scope?.site_id, searchParams, timeWindow?.end, timeWindow?.start]);

  const technicalDetailsPath = useMemo(() => {
    if (!artifact) return '';
    const query = new URLSearchParams();
    const orgId = String(searchParams.get('org_id') || persistedReport?.org_id || '').trim();
    const siteId = firstNonEmpty(searchParams.get('site_id'), scope?.site_id, persistedReport?.site_id);
    const workflowId = firstNonEmpty(searchParams.get('workflow_id'), artifact.workflow_id, persistedReport?.workflow_id);
    const zoneId = firstNonEmpty(searchParams.get('zone_id'), scope?.zones?.[0]);
    const deviceId = firstNonEmpty(searchParams.get('device_id'), scope?.device_id);
    const cameraId = firstNonEmpty(searchParams.get('camera_id'), scope?.camera_id);
    const policyType = firstNonEmpty(searchParams.get('policy_type'), scope?.risk_type);
    const reportFrom = firstNonEmpty(searchParams.get('report_from'), timeWindow?.start);
    const reportTo = firstNonEmpty(searchParams.get('report_to'), timeWindow?.end);
    const policyName = String(searchParams.get('policy_name') || '').trim();
    if (orgId) query.set('org_id', orgId);
    if (siteId) query.set('site_id', siteId);
    if (workflowId) query.set('workflow_id', workflowId);
    if (reportId) query.set('report_id', reportId);
    if (zoneId) query.set('zone_id', zoneId);
    if (deviceId) query.set('device_id', deviceId);
    if (cameraId) query.set('camera_id', cameraId);
    if (policyType) query.set('policy_type', policyType);
    if (policyName) query.set('policy_name', policyName);
    if (reportFrom) query.set('report_from', reportFrom);
    if (reportTo) query.set('report_to', reportTo);
    if (artifactIncidentId) {
      query.set('case_id', artifactIncidentId);
      query.set('incident_id', artifactIncidentId);
    }
    const rendered = query.toString();
    return rendered ? `/acceptance/proof/technical?${rendered}` : '';
  }, [
    artifact,
    artifactIncidentId,
    persistedReport?.org_id,
    persistedReport?.site_id,
    persistedReport?.workflow_id,
    reportId,
    scope?.camera_id,
    scope?.device_id,
    scope?.risk_type,
    scope?.site_id,
    scope?.zones,
    searchParams,
    timeWindow?.end,
    timeWindow?.start,
  ]);

  const { liveCasePath, livePatternPath, liveWorkflowPath } = useMemo(() => resolveSignedScanArtifactReturnPaths({
    caseId: linkedSafetyCase?.safety_case_id,
    findingId: linkedSafetyCase?.finding_id,
    artifactFindingId: artifact?.finding_id,
    querySafetyCaseId: searchParams.get('safety_case_id'),
    queryCaseId: searchParams.get('case_id'),
    orgId: firstNonEmpty(searchParams.get('org_id'), persistedReport?.org_id),
    siteId: firstNonEmpty(searchParams.get('site_id'), searchParams.get('site'), scope?.site_id, persistedReport?.site_id, linkedSafetyCase?.site_id),
    workflowId: firstNonEmpty(liveFollowup?.workflow_id, artifact?.workflow_id, linkedSafetyCase?.workflow_id),
    incidentId: firstNonEmpty(liveFollowup?.newer_representative_incident_id, liveFollowup?.representative_incident_id, artifactIncidentId),
    zoneId: firstNonEmpty(searchParams.get('zone_id'), scope?.zones?.[0]),
    cameraId: firstNonEmpty(searchParams.get('camera_id'), scope?.camera_id),
    deviceId: firstNonEmpty(searchParams.get('device_id'), scope?.device_id),
    policyType: firstNonEmpty(searchParams.get('policy_type'), scope?.risk_type),
    recommendedNextStep: liveFollowup?.recommended_next_step,
  }), [artifact, artifactIncidentId, linkedSafetyCase, liveFollowup, persistedReport, scope, searchParams]);

  const liveFollowupRecord = (liveFollowup || {}) as Record<string, any>;
  const representativeCapturedAt = normalizeOptionalValue(artifactDisplayLabels?.detection_at)
    || timeWindow?.start
    || artifact?.finalized_at
    || '';
  const riskBadgeTone = String(artifactDisplayLabels?.risk_level || '').trim().toLowerCase() === 'high'
    ? 'error'
    : String(artifactDisplayLabels?.risk_level || '').trim().toLowerCase() === 'unclassified'
    ? 'warning'
    : 'info';
  const findingHeadline = normalizeOptionalValue(projectionCustomerSummary?.what_happened_text)
    || normalizeOptionalValue(artifactDisplayLabels?.finding_headline)
    || normalizeOptionalValue(technicalCustomerSummary?.what_happened_text)
    || 'Signed-off safety activity was observed in the reviewed area.';
  const triggerSummary = normalizeOptionalValue(artifactDisplayLabels?.trigger_label) || 'Signed-off detector evidence';
  const validatedClaimText = claimBoundaryPresentation.hasCanonical
    ? claimBoundaryPresentation.claimBoundaryText
    : normalizeOptionalValue(artifactClaimsSummary?.validated_claim_text)
    || normalizeOptionalValue(projectionCustomerSummary?.limitation_text)
    || normalizeOptionalValue(technicalCustomerSummary?.claim_boundary_text)
    || 'Corrective-action effectiveness is not claimed unless validation gates support it.';
  const whatChanged = decisionReadiness?.what_changed || null;
  const criticalExposure = decisionReadiness?.severe_exposure || null;
  const decisionQuality = decisionReadiness?.decision_quality || null;
  const riskDriver = decisionReadiness?.risk_driver || null;
  const recommendedNextAction = decisionReadiness?.recommended_next_action || null;
  const decisionWarnings = Array.isArray(decisionReadiness?.warnings) ? decisionReadiness?.warnings || [] : [];
  const impactMeasurementDetails = impactSummary?.impact_measurement || null;
  const impactUsesSyntheticMidpoint = String(impactMeasurementDetails?.pivot_basis || '').trim().toLowerCase() === 'synthetic_midpoint_split';
  const impactSummaryStatus = String(impactSummary?.status || '').trim().toLowerCase();
  const impactMeasurementStatus = String(
    impactMeasurementDetails?.measurement_status || impactSummary?.measurement_status || impactMeasurementGraph?.summary?.measurement_status || '',
  ).trim().toLowerCase();
  const impactMeasurementWaived = impactMeasurementStatus === 'waived' || impactSummaryStatus === 'waived';
  const whatChangedClaimStrength = String(whatChanged?.claim_strength || '').trim().toLowerCase();
  const whatChangedStatus = String(whatChanged?.status || '').trim().toLowerCase();
  const impactDenominatorBasis = String(
    impactMeasurementDetails?.denominator_basis || impactMeasurementGraph?.summary?.denominator_basis || '',
  ).trim().toLowerCase();
  const impactDenominatorTrust = String(
    impactMeasurementDetails?.denominator_trust || impactMeasurementGraph?.summary?.denominator_trust || '',
  ).trim().toLowerCase();
  const impactDenominatorTrusted = isImpactDenominatorTrusted(impactDenominatorTrust);
  const impactBaselineMonitoredHours = asFiniteNumber(
    impactMeasurementDetails?.baseline_monitored_hours ?? impactMeasurementGraph?.summary?.baseline_monitored_hours,
  );
  const impactFollowupMonitoredHours = asFiniteNumber(
    impactMeasurementDetails?.followup_monitored_hours ?? impactMeasurementGraph?.summary?.followup_monitored_hours,
  );
  const impactShortWindowWarning = buildShortWindowWarning(impactBaselineMonitoredHours, impactFollowupMonitoredHours);
  const percentReduction = asFiniteNumber(impactMeasurementGraph?.summary?.percent_reduction);
  const keyInsightText = normalizeOptionalValue(artifactClaimsSummary?.key_insight_text)
    || (impactDenominatorTrusted && typeof percentReduction === 'number'
      ? `The ${formatNumericValue(Math.abs(percentReduction))}% exposure-rate ${percentReduction >= 0 ? 'reduction' : 'increase'} is a directional signal indicating ${percentReduction >= 0 ? 'improvement' : 'worsening'}, not prevention.`
      : 'Exposure-rate interpretation is not claimed because monitored observation time is unavailable, incomplete, or requires review.');
  const claimConfidenceLabel = claimBoundaryPresentation.hasCanonical
    ? claimBoundaryPresentation.effectivenessClaimLabel
    : normalizeOptionalValue(artifactClaimsSummary?.claim_confidence_label)
    || firstNonEmpty(
      formatTokenLabel(impactSummary?.confidence_level),
      String(technicalCustomerSummary?.measurement_trust_level || '').toLowerCase().includes('directional')
        ? 'Directional'
        : formatTokenLabel(technicalCustomerSummary?.measurement_trust_level),
      'Not available',
    );
  const evidenceSufficiencyLabel = normalizeOptionalValue(artifactClaimsSummary?.evidence_sufficiency_label)
    || normalizeOptionalValue((technicalDetails as Record<string, any> | null)?.evidence_status?.evidence_sufficiency_label)
    || 'Not available';
  const dataNormalizationLabel = normalizeOptionalValue(artifactClaimsSummary?.data_normalization_label)
    || (impactDenominatorTrusted
      ? 'Monitored observation hours'
      : impactDenominatorBasis === 'monitored_observation_time'
      ? 'Monitored observation-time review required'
      : 'Not available');
  const comparisonMethodLabel = normalizeOptionalValue(artifactClaimsSummary?.comparison_method_label)
    || (impactDenominatorBasis === 'monitored_observation_time'
      ? 'Baseline vs. Follow-up (Monitored Observation Time)'
      : 'Baseline vs. Follow-up (Not available)');
  const exposureBasisText = normalizeOptionalValue(artifactClaimsSummary?.exposure_basis_text)
    || (impactDenominatorTrusted
      ? 'Exposure rates are normalized by monitored observation hours. Offline time is excluded from the denominator.'
      : impactDenominatorBasis === 'elapsed_wall_clock'
      ? 'This comparison uses matched elapsed-time windows because monitored observation time was not available. Treat the result as an observational signal rather than a validated exposure-rate claim.'
      : 'No improvement or worsening claim is made until denominator reliability is trusted.');
  const eventBasisLabel = remapSafetyScanCustomerLabel(
    normalizeOptionalValue(artifactDisplayLabels?.event_basis_label) || 'Signed-off scan evidence',
  );
  const scopeReferenceLabel = normalizeOptionalValue(artifactDisplayLabels?.scope_reference_label) || 'Signed-off artifact';
  const impactHourSeries = Array.isArray(impactMeasurementGraph?.series?.hour) ? impactMeasurementGraph?.series?.hour || [] : [];
  const impactBaselineRatePerHour = firstFiniteNumber(
    asFiniteNumber(whatChanged?.baseline_rate_per_hour),
    asFiniteNumber(impactHourSeries.find((item) => item?.id === 'baseline')?.value),
    dailyRateToHourly(asFiniteNumber(impactMeasurementGraph?.summary?.baseline_exposure_rate_per_day)),
    dailyRateToHourly(asFiniteNumber(impactSummary?.baseline_exposure_rate)),
  );
  const impactFollowupRatePerHour = firstFiniteNumber(
    asFiniteNumber(whatChanged?.followup_rate_per_hour),
    asFiniteNumber(impactHourSeries.find((item) => item?.id === 'followup')?.value),
    dailyRateToHourly(asFiniteNumber(impactMeasurementGraph?.summary?.post_action_exposure_rate_per_day)),
    dailyRateToHourly(asFiniteNumber(impactSummary?.post_action_exposure_rate)),
  );
  const impactRateComparisonAvailable = impactBaselineRatePerHour !== null
    && impactFollowupRatePerHour !== null;
  const normalizedRateComparisonAvailable = impactDenominatorBasis === 'monitored_observation_time'
    && impactDenominatorTrusted
    && impactRateComparisonAvailable
    && !impactMeasurementWaived
    && whatChangedClaimStrength !== 'insufficient'
    && whatChangedStatus === 'available';
  const impactAuditContextRatesAvailable = impactDenominatorBasis === 'monitored_observation_time'
    && !impactDenominatorTrusted
    && impactRateComparisonAvailable
    && !impactMeasurementWaived;
  const showWhatChangedRates = !outcomeContext.isDemoOrLoopedSource
    && impactDenominatorTrusted
    && whatChangedStatus === 'available'
    && whatChangedClaimStrength !== 'insufficient'
    && asFiniteNumber(whatChanged?.baseline_rate_per_hour) !== null
    && asFiniteNumber(whatChanged?.followup_rate_per_hour) !== null;
  const showWhatChangedAuditRates = !outcomeContext.isDemoOrLoopedSource && impactAuditContextRatesAvailable;
  const whatChangedSummaryText = !impactDenominatorTrusted && !impactMeasurementWaived
    ? 'Impact comparison requires trusted monitored observation-time coverage before an improvement or worsening claim can be made.'
    : impactUsesSyntheticMidpoint
    ? 'Directional comparison across signed-off midpoint windows is retained for this report.'
    : formatSpecifiedValue(whatChanged?.summary);
  const decisionMissingInputsDisplay = (() => {
    if (outcomeContext.isDemoOrLoopedSource && outcomeContext.missingInputReplacement) {
      return [outcomeContext.missingInputReplacement];
    }
    const missingInputs = decisionQuality?.missing_inputs;
    const base = Array.isArray(missingInputs) ? [...missingInputs] : [];
    if (impactShortWindowWarning && !base.some((item) => item.toLowerCase().includes('short-window'))) {
      base.push(impactShortWindowWarning);
    }
    return base;
  })();
  const rawExecutiveRiskFound = firstNonEmpty(
    normalizeOptionalValue(projectionCustomerSummary?.what_happened_text),
    normalizeOptionalValue(artifactDisplayLabels?.finding_headline),
    normalizeOptionalValue(riskDriver?.summary),
    normalizeOptionalValue(technicalCustomerSummary?.what_happened_text),
    normalizeOptionalValue(artifactDisplayLabels?.activity_type_label),
  );
  const executiveRiskFoundText = rawExecutiveRiskFound
    ? formatSpecifiedValue(rawExecutiveRiskFound)
    : 'Risk finding not available in signed-off package.';
  const executiveSevereExposureText = normalizeOptionalValue(criticalExposure?.summary)
    ? formatSpecifiedValue(criticalExposure?.summary)
    : 'Severe-exposure classification requires review.';
  const impactPostureSummary = buildImpactPostureCopy({
    outcomeContext,
    impactWaived: impactMeasurementWaived,
    denominatorTrust: impactDenominatorTrust,
    measurementStatus: impactMeasurementStatus,
    normalizedComparisonAvailable: normalizedRateComparisonAvailable,
    hasImpactData: Boolean(impactMeasurementGraph?.summary || impactMeasurementDetails || impactSummary || whatChanged),
  });
  const executiveImpactPostureText = outcomeContext.isDemoOrLoopedSource
    ? outcomeContext.outcomeStatusLabel
    : impactPostureSummary;
  const executiveDecisionReadinessText = normalizeOptionalValue(decisionQuality?.summary)
    ? formatSpecifiedValue(decisionQuality?.summary)
    : 'Decision readiness requires review.';
  const executiveNextActionText = normalizeOptionalValue(recommendedNextAction?.summary)
    ? formatSpecifiedValue(recommendedNextAction?.summary)
    : Array.isArray(recommendedNextAction?.actions) && recommendedNextAction?.actions?.length
    ? formatSpecifiedValue(recommendedNextAction?.actions?.[0]?.label)
    : 'Not specified.';
  const executiveEvidenceText = buildExecutiveEvidenceSummary(evidenceSummary);
  const safetyCaseLabel = buildSafetyCaseLabel({
    activityTypeLabel: artifactActivityTypeLabel,
    findingHeadline: normalizeOptionalValue(artifactDisplayLabels?.finding_headline),
    riskFound: executiveRiskFoundText,
  });
  const representativeEvidenceNote = buildRepresentativeEvidenceNote({
    activityTypeLabel: artifactActivityTypeLabel,
    areaLabel: artifactScopeAreaLabel,
    findingHeadline: normalizeOptionalValue(artifactDisplayLabels?.finding_headline),
  });
  const executiveFindingSupportText = [
    normalizeOptionalValue(buyerExecutiveFinding?.peak_period)
      ? `Peak activity occurred at ${buyerExecutiveFinding?.peak_period}.`
      : '',
    normalizeOptionalValue(buyerExecutiveFinding?.primary_hotspot)
      ? `Primary area: ${formatZoneDisplayLabel(String(buyerExecutiveFinding?.primary_hotspot || ''))}.`
      : '',
  ].filter(Boolean).join(' ');
  const topSnapshotFacts = [
    {
      label: 'Site',
      primary: artifactSiteLabel,
      secondary: null,
      icon: BusinessOutlined,
      tone: '#2563eb',
    },
    {
      label: 'Operating area',
      primary: artifactScopeAreaLabel,
      secondary: null,
      icon: LocationOnOutlined,
      tone: '#2563eb',
    },
    {
      label: 'Scan window',
      primary: formatCompactWindowRange(signedOffReportScopeWindow.start, signedOffReportScopeWindow.end),
      secondary: null,
      icon: CalendarTodayOutlined,
      tone: '#2563eb',
      testId: 'top-scan-window-value',
      rawStart: signedOffReportScopeWindow.start,
      rawEnd: signedOffReportScopeWindow.end,
    },
    {
      label: 'Package status',
      primary: formatTokenLabel(artifact?.status || exportManifest?.report_status || 'finalized'),
      secondary: artifact?.finalized_at ? formatCompactDateTime(artifact.finalized_at) : null,
      icon: Inventory2Outlined,
      tone: '#2563eb',
    },
    {
      label: 'Workflow status',
      primary: outcomeContext.isDemoOrLoopedSource
        ? outcomeContext.workflowStatusLabel
        : impactPostureSummary,
      secondary: outcomeContext.isDemoOrLoopedSource
        ? outcomeContext.workflowStatusDetail
        : impactMeasurementWaived
        ? null
        : impactDenominatorTrusted
        ? 'Directional comparison retained'
        : 'Impact measurement requires monitored-time review',
      tertiary: outcomeContext.isDemoOrLoopedSource
        ? `${outcomeContext.exposureReductionLabel}. Looped footage cannot show behavioral change after corrective action. ${outcomeContext.productionMeasurementLabel}.`
        : null,
      icon: outcomeContext.isDemoOrLoopedSource ? TrackChangesOutlined : WarningAmberOutlined,
      tone: outcomeContext.isDemoOrLoopedSource ? '#2563eb' : '#d97706',
    },
  ];
  const followupHasNewerActivity = Boolean(liveFollowup?.has_new_activity_since_report);
  const followupLatestStatus = followupHasNewerActivity
    ? firstNonEmpty(
      normalizeOptionalValue(liveFollowupRecord.workflow_status_label),
      normalizeOptionalValue(liveFollowupRecord.workflow_status),
      'Not available',
    )
    : 'No newer workflow activity';
  const followupLatestTimeRaw = firstNonEmpty(
    normalizeOptionalValue(liveFollowupRecord.latest_activity_at),
    normalizeOptionalValue(liveFollowupRecord.updated_at),
    normalizeOptionalValue(liveFollowupRecord.source_workflow_updated_at),
  );
  const followupLatestTime = followupHasNewerActivity && followupLatestTimeRaw ? formatDateTime(followupLatestTimeRaw) : 'Not available';
  const followupReviewStatus = followupHasNewerActivity
    ? firstNonEmpty(
      normalizeOptionalValue(liveFollowupRecord.latest_review_status_label),
      normalizeOptionalValue(liveFollowupRecord.review_status_label),
      normalizeOptionalValue(liveFollowupRecord.review_status),
      'Not available',
    )
    : 'Not available';
  const followupRecentChanges = followupHasNewerActivity
    ? firstNonEmpty(
      normalizeOptionalValue(liveFollowupRecord.recent_changes_label),
      normalizeOptionalValue(liveFollowupRecord.recommended_next_step_label),
      'No changes',
    )
    : 'No changes';
  const repeatSafetyScanPath = useMemo(() => {
    if (!canConfigureScheduledScans) return null;
    return buildRepeatSafetyScanPath({
      sourceWorkflowId: firstNonEmpty(
        normalizeOptionalValue(artifact?.workflow_id),
        normalizeOptionalValue(openData?.report?.workflow_id),
        normalizeOptionalValue(searchParams.get('workflow_id')),
      ),
      siteId: firstNonEmpty(
        normalizeOptionalValue(artifact?.scope?.site_id as string | undefined),
        normalizeOptionalValue(openData?.report?.site_id),
        normalizeOptionalValue(searchParams.get('site_id')),
      ),
      windowStart: signedOffReportScopeWindow.start,
      windowEnd: signedOffReportScopeWindow.end,
      cameraId: firstNonEmpty(
        normalizeOptionalValue(searchParams.get('camera_id')),
        normalizeOptionalValue(artifact?.scope?.camera_id as string | undefined),
      ),
      cameraLabel: normalizeOptionalValue(searchParams.get('camera_label')),
      zoneId: firstNonEmpty(
        normalizeOptionalValue(searchParams.get('zone_id')),
        Array.isArray(artifact?.scope?.zones) ? normalizeOptionalValue(artifact?.scope?.zones?.[0] as string | undefined) : '',
      ),
      zoneLabel: artifactScopeAreaLabel,
      riskType: firstNonEmpty(
        normalizeOptionalValue(searchParams.get('policy_type')),
        normalizeOptionalValue(artifact?.scope?.risk_type as string | undefined),
      ),
      name: firstNonEmpty(
        normalizeOptionalValue(searchParams.get('policy_name')),
        normalizeOptionalValue(searchParams.get('risk_pattern_label')),
        artifactActivityTypeLabel,
      ),
      safetyCaseId: firstNonEmpty(
        normalizeOptionalValue(searchParams.get('safety_case_id')),
        normalizeOptionalValue(artifact?.finding_id),
        normalizeOptionalValue(openData?.report?.finding_id),
      ),
    });
  }, [
    artifact?.finding_id,
    artifact?.scope,
    artifact?.workflow_id,
    artifactActivityTypeLabel,
    artifactScopeAreaLabel,
    canConfigureScheduledScans,
    openData?.report?.finding_id,
    openData?.report?.site_id,
    openData?.report?.workflow_id,
    searchParams,
    signedOffReportScopeWindow.end,
    signedOffReportScopeWindow.start,
  ]);
  const summaryCalloutText = [
    outcomeContext.isDemoOrLoopedSource
      ? outcomeContext.demoBannerText
      : (impactUsesSyntheticMidpoint
        ? 'This report uses a signed-off 50% midpoint split to frame baseline and follow-up comparison windows. Corrective-action effectiveness is not claimed unless validation gates support it.'
        : normalizeOptionalValue(artifactClaimsSummary?.callout_text))
        || `${firstNonEmpty(technicalCustomerSummary?.corrective_action_status_text, 'Corrective action completed as reported.')}${technicalCustomerSummary?.impact_status_text ? ` ${technicalCustomerSummary.impact_status_text}.` : impactMeasurementWaived ? ' Impact measurement was waived for this report.' : ' Impact measurement is not claim-qualified yet.'} Corrective-action effectiveness is not claimed unless validation gates support it.`,
    outcomeContext.isDemoOrLoopedSource ? null : impactShortWindowWarning,
  ].filter(Boolean).join(' ');
  const handleDownloadZip = async () => {
    if (!reportId) return;
    setDownloadLoading('zip');
    setDownloadError(null);
    try {
      const { blob, filename } = await reportsApi.downloadSafetyReportArtifact(reportId);
      downloadBlob(blob, filename);
    } catch (err: any) {
      setDownloadError(err?.message || 'Unable to download the report ZIP.');
    } finally {
      setDownloadLoading(null);
    }
  };

  const handleDownloadExternalSafeZip = async () => {
    if (!reportId) return;
    setDownloadLoading('zip');
    setDownloadError(null);
    try {
      const { blob, filename } = await reportsApi.downloadExternalSafetyScanPackage(reportId);
      downloadBlob(blob, filename);
    } catch (err: any) {
      setDownloadError(err?.message || 'Unable to download the external-safe report package.');
    } finally {
      setDownloadLoading(null);
    }
  };

  const handleDownloadPdf = async (face: 'customer_compact' | 'detailed' = 'customer_compact') => {
    if (!reportId) return;
    setDownloadLoading(face === 'detailed' ? 'pdf_detailed' : 'pdf');
    setDownloadError(null);
    try {
      if (canDownloadExternalSafePdf && !canDownloadCustomer) {
        const { blob, filename } = await reportsApi.downloadExternalSafetyScanPdf(reportId);
        downloadBlob(blob, filename);
        return;
      }
      const { blob, filename } = await reportsApi.downloadSafetyReportArtifactPdf(
        reportId,
        compactBuyerFace ? { face } : undefined,
      );
      downloadBlob(blob, filename);
    } catch (err: any) {
      setDownloadError(
        err?.message
        || (face === 'detailed'
          ? 'Unable to download the detailed signed artifact PDF.'
          : 'Unable to download the customer scan PDF.'),
      );
    } finally {
      setDownloadLoading(null);
    }
  };

  const handleDownloadDocx = async () => {
    if (!reportId) return;
    setDownloadLoading('docx');
    setDownloadError(null);
    try {
      const { blob, filename } = await reportsApi.downloadSafetyReportArtifactDocx(reportId);
      downloadBlob(blob, filename);
    } catch (err: any) {
      setDownloadError(err?.message || 'Unable to download the report DOCX.');
    } finally {
      setDownloadLoading(null);
    }
  };

  const handleDownloadCsv = async () => {
    if (!reportId) return;
    setDownloadLoading('csv');
    setDownloadError(null);
    try {
      const { blob, filename } = await reportsApi.downloadSafetyReportArtifactCsv(reportId);
      downloadBlob(blob, filename);
    } catch (err: any) {
      setDownloadError(err?.message || 'Unable to download the report CSV.');
    } finally {
      setDownloadLoading(null);
    }
  };

  if (!reportId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Missing report id.</Alert>
      </Box>
    );
  }

  if (!bootstrapLoading && !artifactLoading && !artifactData) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{bootstrapError || artifactError || 'Unable to load the Signed Safety Scan artifact.'}</Alert>
      </Box>
    );
  }

  if (!artifact) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">No artifact details were returned for this report.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }} data-testid="safety-report-artifact-detail">
      <SafetyReportArtifactAdvancedBanner reportId={reportId} />
      <SafetyManagementLineageBreadcrumbs
        ariaLabel="Safety Scan parentage"
        testId="safety-scan-lineage-breadcrumbs"
        crumbs={[
          { label: 'Safety Management', href: reportsBackPath },
          {
            label: liveCasePath
              ? `Safety Case · ${firstNonEmpty(
                String(linkedSafetyCase?.scope?.risk_type || ''),
                linkedSafetyCase?.finding_type,
                'Safety Case',
              ).replace(/_/g, ' ')}`
              : 'Managed Safety Case: Not created',
            href: liveCasePath || null,
          },
          {
            label: `Safety Scan · ${firstNonEmpty(
              formatCompactDateTime(timeWindow?.start),
              reportId,
              'Current',
            )}`,
          },
        ]}
      />
      <Stack direction={{ xs: 'column', lg: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 3, mt: 2 }}>
        <Stack spacing={0.5}>
          <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
            <Typography variant="h4">Advanced package</Typography>
            <Chip icon={<CheckCircleOutlineOutlined />} label="Signed-off" color="info" variant="outlined" />
            <Chip icon={<LockOutlined />} label="Read-only" variant="outlined" />
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Signed-off safety intelligence artifact generated from selected camera evidence.
          </Typography>
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap flexWrap="wrap">
          {repeatSafetyScanPath ? (
          <Button
            variant="outlined"
            startIcon={<CalendarTodayOutlined />}
            onClick={() => navigate(repeatSafetyScanPath)}
          >
            Schedule this Safety Scan
          </Button>
          ) : null}
          {canDownloadInternal ? (
          <Button
            variant="contained"
            onClick={() => void handleDownloadZip()}
            disabled={Boolean(downloadLoading)}
            startIcon={<DownloadOutlined />}
            sx={{ textTransform: 'none' }}
            aria-label="Download Report Package"
          >
            {downloadLoading === 'zip' ? 'Downloading Report Package...' : 'Download Report Package'}
          </Button>
          ) : null}
          {!canDownloadInternal && canDownloadExternalSafe ? (
          <Button
            variant="contained"
            onClick={() => void handleDownloadExternalSafeZip()}
            disabled={Boolean(downloadLoading)}
            startIcon={<DownloadOutlined />}
            sx={{ textTransform: 'none' }}
            aria-label="Download External-Safe Package"
          >
            {downloadLoading === 'zip' ? 'Downloading External-Safe Package...' : 'Download External-Safe Package'}
          </Button>
          ) : null}
          {canViewAppendix && technicalDetailsPath ? (
            <Button variant="outlined" onClick={() => navigate(technicalDetailsPath)} sx={{ textTransform: 'none' }}>
              Technical Details
            </Button>
          ) : null}
          {liveWorkflowPath ? (
            <Button
              variant="outlined"
              onClick={() => navigate(liveWorkflowPath)}
              endIcon={<ArrowOutwardOutlined />}
              sx={{ textTransform: 'none' }}
              aria-label="Open Current Workflow"
              data-testid="artifact-open-current-workflow"
            >
              Open Current Workflow
            </Button>
          ) : null}
          <ArtifactReviewPatternButton
            liveCasePath={liveCasePath}
            livePatternPath={livePatternPath}
            onNavigate={navigate}
          />
        </Stack>
      </Stack>

      <Box
        sx={{
          mb: 3,
          px: { xs: 1.5, md: 2 },
          py: 1.25,
          borderRadius: 2,
          border: '1px solid #E2E8F0',
          bgcolor: '#F8FAFC',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          <Box component="span" sx={{ fontWeight: 700, color: '#334155' }}>
            Signed Safety Scan artifact.
          </Box>
          {' '}
          This report reflects the finalized scan package. Changes in the Safety Case will not change this artifact.
        </Typography>
      </Box>

      {outcomeContext.isDemoOrLoopedSource ? (
        <Alert severity="info" icon={<InfoOutlined />} sx={{ mb: 3 }} data-testid="demo-source-banner">
          {outcomeContext.demoBannerText}
        </Alert>
      ) : null}

      <Card sx={{ mb: 3, borderRadius: 3, boxShadow: '0 8px 24px rgba(15, 23, 42, 0.04)' }}>
        <CardContent sx={{ p: 0 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(5, minmax(0, 1fr))' },
            }}
          >
            {topSnapshotFacts.map((fact, index) => {
              const Icon = fact.icon;
              return (
                <Box
                  key={fact.label}
                  sx={{
                    px: 2.2,
                    py: 2,
                    borderRight: { xs: 'none', md: index < topSnapshotFacts.length - 1 ? '1px solid #E5E7EB' : 'none' },
                    borderBottom: { xs: index < topSnapshotFacts.length - 1 ? '1px solid #E5E7EB' : 'none', md: 'none' },
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <Icon sx={{ color: fact.tone, fontSize: 22, mt: '2px' }} />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="caption" color="text.secondary">
                        {fact.label}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ mt: 0.35, fontWeight: 700 }}
                        data-testid={fact.testId}
                        data-window-start={fact.rawStart}
                        data-window-end={fact.rawEnd}
                      >
                        {fact.primary}
                      </Typography>
                      {fact.secondary ? (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.35 }}>
                          {fact.secondary}
                        </Typography>
                      ) : null}
                      {'tertiary' in fact && fact.tertiary ? (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.35 }}>
                          {fact.tertiary}
                        </Typography>
                      ) : null}
                    </Box>
                  </Stack>
                </Box>
              );
            })}
          </Box>
        </CardContent>
      </Card>
      {downloadError ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          {downloadError}
        </Alert>
      ) : null}

      {artifactLoading && !hasFullArtifact ? (
        <Alert severity="info" sx={{ mb: 3 }} data-testid="artifact-detail-loading-banner">
          Signed scan header loaded. Remaining sections will appear as the full artifact package finishes loading.
        </Alert>
      ) : null}

      {artifactError && !hasFullArtifact ? (
        <Alert severity="warning" sx={{ mb: 3 }} data-testid="artifact-detail-partial-warning">
          {artifactError} Basic report metadata is still available below.
        </Alert>
      ) : null}

      {hasFullArtifact ? (
        <>
      {compactBuyerFace ? (
        <BuyerReportSection
          buyerFacing
          data={safetyScanBuyerReport}
          outcomeContext={outcomeContext}
          safetyScanReportV2={safetyScanReportV2}
        />
      ) : (
        <>
      <ExecutiveDecisionSummaryCard
        riskFound={executiveRiskFoundText}
        severeExposure={executiveSevereExposureText}
        evidence={executiveEvidenceText}
        impactPosture={executiveImpactPostureText}
        decisionReadiness={executiveDecisionReadinessText}
        nextAction={executiveNextActionText}
        traceabilitySource={evidenceSummary.source}
        safetyCaseLabel={safetyCaseLabel}
        findingSupport={executiveFindingSupportText}
      />

      <ArtifactReportAlignmentCards artifactData={artifactData} />

      <WorkplaceConditionObservedSection
        workplaceCondition={workplaceConditionFields}
        conditionContext={workplaceConditionContext}
      />

      <ClosureSummaryArtifactSection persistedReport={persistedReport} />

      <SafetyControlReviewV9ParitySection artifactData={artifactData} />

      <BuyerReportSection
        data={safetyScanBuyerReport}
        outcomeContext={outcomeContext}
        safetyScanReportV2={safetyScanReportV2}
      />
        </>
      )}

      {compactBuyerFace ? (
      <Accordion
        defaultExpanded={false}
        disableGutters
        elevation={0}
        data-testid="artifact-technical-details-accordion"
        sx={{
          mb: 3,
          border: '1px solid #E2E8F0',
          borderRadius: 3,
          '&:before': { display: 'none' },
          overflow: 'hidden',
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreOutlined />} sx={{ px: 2, minHeight: 52 }}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Technical details and methodology
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Executive summary, measurement charts, decision readiness, and full evidence gallery.
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 2, pt: 0, pb: 2 }}>
      <ExecutiveDecisionSummaryCard
        riskFound={executiveRiskFoundText}
        severeExposure={executiveSevereExposureText}
        evidence={executiveEvidenceText}
        impactPosture={executiveImpactPostureText}
        decisionReadiness={executiveDecisionReadinessText}
        nextAction={executiveNextActionText}
        traceabilitySource={evidenceSummary.source}
        safetyCaseLabel={safetyCaseLabel}
        findingSupport={executiveFindingSupportText}
      />

      <ArtifactReportAlignmentCards artifactData={artifactData} />

      <WorkplaceConditionObservedSection
        workplaceCondition={workplaceConditionFields}
        conditionContext={workplaceConditionContext}
      />

      <ClosureSummaryArtifactSection persistedReport={persistedReport} />

      <SafetyControlReviewV9ParitySection artifactData={artifactData} />
        </AccordionDetails>
      </Accordion>
      ) : null}

      {!compactBuyerFace ? (
        <>
      {recommendationSnapshotItems.length ? (
        <SectionCard
          title="Frozen recommendations"
          subtitle="Recommendation decision state was captured at sign-off and remains historical for this signed artifact."
        >
          <Stack spacing={2}>
            {recommendationSnapshotItems.map((recommendation, index) => (
              <Box key={String(recommendation.recommendation_id || recommendation.generated_at || recommendation.original_recommendation_text || index)}>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1 }}>
                  <Chip
                    size="small"
                    color={String(recommendation.current_decision_state || '').trim() === 'accepted' ? 'success' : 'default'}
                    label={recommendation.current_decision_state_label || formatTokenLabel(recommendation.current_decision_state)}
                  />
                  <Chip size="small" variant="outlined" label={formatTokenLabel(recommendation.priority)} />
                  <Chip size="small" variant="outlined" label={formatTokenLabel(recommendation.control_level)} />
                </Stack>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {recommendation.original_recommendation_text || recommendation.recommendation_text || 'Recommendation'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Effective text: {recommendation.effective_recommendation_text || recommendation.current_recommendation_text || recommendation.original_recommendation_text || 'Not recorded'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Latest decision: {recommendation.latest_decision?.decision_state_label || 'Pending'}
                  {recommendation.latest_decision?.decision_reason ? ` · ${recommendation.latest_decision.decision_reason}` : ''}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Linked corrective action: {recommendation.linked_corrective_action?.title || recommendation.linked_corrective_action?.action_id || 'Not linked'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Frozen at {formatDateTime(recommendationSnapshot?.frozen_at || artifact?.finalized_at)}
                </Typography>
              </Box>
            ))}
          </Stack>
        </SectionCard>
      ) : null}

      <Card sx={{ mb: 3, borderRadius: 3, boxShadow: '0 8px 24px rgba(15, 23, 42, 0.04)' }}>
        <CardContent>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            sx={{ mb: 2 }}
          >
            <Box>
              <Typography variant="h6" gutterBottom sx={{ mb: 0.5 }}>
                Evidence and measurement
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Buyer-facing evidence counts, measurement windows, and retained trend visuals derived from the signed-off package.
              </Typography>
            </Box>
            <Chip icon={<LockOutlined />} label="Read-only" size="small" variant="outlined" />
          </Stack>

          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: { xs: '1fr', xl: 'minmax(0, 0.92fr) minmax(0, 1.08fr)' },
            }}
          >
            <Stack spacing={2}>
              <EvidenceCoverageCard summary={evidenceSummary} />
              <AnalyticsSummaryCard visual={hotspotVisual} tone="blue" />
            </Stack>
            <ReportWindowsCard
              visual={reportWindowsVisual}
              reportScopeStart={signedOffReportScopeWindow.start}
              reportScopeEnd={signedOffReportScopeWindow.end}
              baselineWindowStart={correctiveActionImpactTimeline.baseline_window.start}
              baselineWindowEnd={correctiveActionImpactTimeline.baseline_window.end}
              followupWindowStart={correctiveActionImpactTimeline.followup_window.start}
              followupWindowEnd={correctiveActionImpactTimeline.followup_window.end}
              areaLabel={artifactScopeAreaLabel}
              comparisonMethod="Normalized exposure rates"
              syntheticPivot={impactUsesSyntheticMidpoint}
              readOnly
              timeline={correctiveActionImpactTimeline}
              partialStateMessages={correctiveActionImpactTimeline.partial_state_messages.filter((message) => {
                const normalized = String(message || '').trim().toLowerCase();
                if (!normalized) return false;
                const demoLabel = outcomeContext.isDemoOrLoopedSource
                  ? String(outcomeContext.timelineIllustrationLabel || '').trim().toLowerCase()
                  : '';
                if (demoLabel && normalized === demoLabel) return false;
                return true;
              })}
              demoIllustrationLabel={
                outcomeContext.isDemoOrLoopedSource ? outcomeContext.timelineIllustrationLabel : null
              }
            />
          </Box>
          <Box
            sx={{
              mt: 2,
              display: 'grid',
              gap: 2,
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
            }}
          >
            <AnalyticsSummaryCard visual={timePatternVisual} tone="orange" mode="bars" />
            <AnalyticsSummaryCard visual={eventBreakdownVisual} tone="orange" />
          </Box>
        </CardContent>
      </Card>

      <CorrectiveActionImpactTimelineSection
        variant="supplement"
        timeline={correctiveActionImpactTimeline}
        outcomeContext={outcomeContext}
      />

      <ComparisonReadinessSection
        data={comparativeIntelligence}
        denominatorTrust={impactDenominatorTrust}
        timePatternVisual={timePatternVisual}
      />

      <MeasurementAssurancePanel reportId={reportId} />

      <DecisionReadinessSection
        decisionReadiness={decisionReadiness}
        canonicalClaimBoundary={claimBoundaryPresentation.canonicalBoundary}
        outcomeContext={outcomeContext}
        impactDenominatorTrusted={impactDenominatorTrusted}
        impactMeasurementWaived={impactMeasurementWaived}
        showWhatChangedRates={showWhatChangedRates}
        showWhatChangedAuditRates={showWhatChangedAuditRates}
        whatChangedSummaryText={whatChangedSummaryText}
        whatChanged={whatChanged}
        impactBaselineRatePerHour={impactBaselineRatePerHour}
        impactFollowupRatePerHour={impactFollowupRatePerHour}
        decisionMissingInputsDisplay={decisionMissingInputsDisplay}
        decisionWarnings={decisionWarnings}
      />

      <ValidationHandoffCard
        blockers={decisionMissingInputsDisplay}
        liveWorkflowPath={liveWorkflowPath}
        onOpenWorkflow={liveWorkflowPath ? () => navigate(liveWorkflowPath) : undefined}
        liveStatus={followupLatestStatus}
        liveRecentChanges={followupRecentChanges}
      />

      <Box sx={{ mb: 3 }}>
        <EvidenceGallery gallery={evidenceGallery} evidenceSummary={evidenceSummary} evidenceNote={representativeEvidenceNote} />
      </Box>
        </>
      ) : null}

      <Accordion
        defaultExpanded={false}
        disableGutters
        elevation={0}
        sx={{
          mb: 3,
          border: '1px solid #E2E8F0',
          borderRadius: 3,
          '&:before': { display: 'none' },
          overflow: 'hidden',
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreOutlined />} sx={{ px: 2, minHeight: 52 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Report details and quick actions
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 2, pt: 0, pb: 2 }}>
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(4, minmax(0, 1fr))' },
            }}
          >
            <SignedOffScopeCard
              site={artifactSiteLabel}
              area={artifactScopeAreaLabel}
              activityType={artifactActivityTypeLabel}
              eventBasis={eventBasisLabel}
              scopeRef={scopeReferenceLabel}
              startTime={formatDateTime(signedOffReportScopeWindow.start)}
              startTimeRaw={signedOffReportScopeWindow.start}
              endTime={formatDateTime(signedOffReportScopeWindow.end)}
              endTimeRaw={signedOffReportScopeWindow.end}
            />
            <ReportPackageCard
              preparationStatus={normalizeOptionalValue(packageDisplay?.preparation_status_label) || 'Completed'}
              reportStatus={normalizeOptionalValue(packageDisplay?.report_status_label) || formatTokenLabel(exportManifest?.report_status || technicalDetails?.review_status?.report_status || 'finalized')}
              packageType={normalizeOptionalValue(packageDisplay?.package_type_label) || exportManifest?.package_type || 'safety_report'}
              packageFiles={packageFilesLabel}
              onDownloadZip={() => void (canDownloadInternal ? handleDownloadZip() : handleDownloadExternalSafeZip())}
              showDownloadButton={canDownloadInternal || canDownloadExternalSafe}
              downloadDisabled={Boolean(downloadLoading)}
              downloadLoading={downloadLoading === 'zip'}
            />
            <FollowUpActivityCard
              hasNewerActivity={followupHasNewerActivity}
              latestStatus={followupLatestStatus}
              latestTime={followupLatestTime}
              reviewStatus={followupReviewStatus}
              recentChanges={followupRecentChanges}
            />
            <QuickActionsCard
              onOpenWorkflow={liveWorkflowPath ? () => navigate(liveWorkflowPath) : undefined}
              onBackToReports={() => navigate(reportsBackPath)}
            />
          </Box>
        </AccordionDetails>
      </Accordion>

      {linkedSafetyCase ? (
        <Card sx={{ mb: 3, borderRadius: 3, boxShadow: '0 8px 24px rgba(15, 23, 42, 0.04)' }}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
              Linked Safety Case
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              This Signed Safety Scan is linked to a Safety Case. Future corrective-action activity and learning continue there and appear only in later Safety Scans.
            </Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} justifyContent="space-between">
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {String(
                    linkedSafetyCase.scope?.risk_type
                    || linkedSafetyCase.hazard_category
                    || linkedSafetyCase.finding_type
                    || 'Safety Case',
                  ).replace(/_/g, ' ')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {linkedSafetyCase.safety_case_status_label || linkedSafetyCase.safety_case_status || 'Open'}
                </Typography>
                {(linkedSafetyCase.outcome_learning_summary?.highlights || [])[0] ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                    {`Latest learning: ${(linkedSafetyCase.outcome_learning_summary?.highlights || [])[0]}`}
                  </Typography>
                ) : null}
              </Box>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                <Chip size="small" label={`${linkedSafetyCase.recommendations?.length || 0} recommendations`} />
                <Chip size="small" variant="outlined" label={`${linkedSafetyCase.outcome_learning_summary?.validated_count || 0} validated outcomes`} />
              </Stack>
            </Stack>
            <ArtifactOpenLinkedSafetyCaseButton liveCasePath={liveCasePath} onNavigate={navigate} />
          </CardContent>
        </Card>
      ) : null}

      {canShowExportFormats ? (
      <Card sx={{ mb: 3, borderRadius: 3, boxShadow: '0 8px 24px rgba(15, 23, 42, 0.04)' }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
            Export formats
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {canDownloadCustomer
              ? (compactBuyerFace
                ? 'Download the Customer Safety Scan, or the technical report. DOCX and CSV remain available from the same package.'
                : 'Download PDF, DOCX, or CSV from the same signed-off package.')
              : 'Download external-safe PDF from the signed-off package.'}
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {canDownloadCustomer && compactBuyerFace ? (
              <>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => void handleDownloadPdf('customer_compact')}
                  disabled={Boolean(downloadLoading)}
                  startIcon={<PictureAsPdfOutlined />}
                  sx={{ textTransform: 'none', px: 1.5 }}
                  aria-label="Download Customer Safety Scan"
                  data-testid="download-customer-scan-pdf"
                >
                  {downloadLoading === 'pdf' ? 'Preparing…' : 'Download Customer Safety Scan'}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => void handleDownloadPdf('detailed')}
                  disabled={Boolean(downloadLoading)}
                  startIcon={<PictureAsPdfOutlined color="error" />}
                  sx={{ textTransform: 'none', px: 1.5 }}
                  aria-label="Download Technical Report"
                  data-testid="download-detailed-signed-pdf"
                >
                  {downloadLoading === 'pdf_detailed' ? 'Preparing…' : 'Download Technical Report'}
                </Button>
              </>
            ) : (
              <Button
                variant="outlined"
                size="small"
                onClick={() => void handleDownloadPdf()}
                disabled={Boolean(downloadLoading)}
                startIcon={<PictureAsPdfOutlined color="error" />}
                sx={{ textTransform: 'none', px: 1.5 }}
                aria-label="PDF"
              >
                PDF
              </Button>
            )}
            {canDownloadDocxCsv ? (
            <>
            <Button
              variant="outlined"
              size="small"
              onClick={() => void handleDownloadDocx()}
              disabled={Boolean(downloadLoading)}
              startIcon={<DescriptionOutlined sx={{ color: '#2563eb' }} />}
              sx={{ textTransform: 'none', px: 1.5 }}
              aria-label="DOCX"
            >
              DOCX
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => void handleDownloadCsv()}
              disabled={Boolean(downloadLoading)}
              startIcon={<TableChartOutlined sx={{ color: '#16a34a' }} />}
              sx={{ textTransform: 'none', px: 1.5 }}
              aria-label="CSV"
            >
              CSV
            </Button>
            </>
            ) : null}
          </Stack>
        </CardContent>
      </Card>
      ) : null}

      {canViewAppendix ? (
      <Accordion
        sx={{ mb: 3, borderRadius: 3, border: '1px solid', borderColor: '#E5E7EB', boxShadow: '0 8px 24px rgba(15, 23, 42, 0.04)', '&:before': { display: 'none' } }}
      >
        <AccordionSummary expandIcon={<ExpandMoreOutlined />} aria-controls="detailed-report-records-content" id="detailed-report-records-header">
          <Box>
            <Typography variant="h6">Technical appendix and EHS worksheet</Typography>
            <Typography variant="body2" color="text.secondary">
              Audit-ready evidence, EHS worksheet detail, traceability fields, and detailed signed-off records retained for compliance review.
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          <EhsReviewWorksheetCard worksheet={ehsReviewWorksheet} />
          <Box
            sx={{
              mb: 3,
              display: 'grid',
              gap: 2,
              gridTemplateColumns: { xs: '1fr', xl: 'minmax(0, 0.9fr) minmax(0, 1.1fr)' },
            }}
          >
            <SafetyScanFindingCard
              findingText={findingHeadline}
              riskDescription={normalizeOptionalValue(artifactDisplayLabels?.risk_description) || 'Risk classification was not retained in the signed-off artifact.'}
              riskBadgeLabel={normalizeOptionalValue(artifactDisplayLabels?.risk_badge_label) || 'Not classified'}
              riskBadgeTone={riskBadgeTone}
              detectionDate={formatDateTime(representativeCapturedAt)}
              location={artifactScopeAreaLabel}
              triggerText={triggerSummary}
            />
            <ImpactMeasurementCard
              visual={impactMeasurementVisual}
              graph={impactMeasurementGraph}
              selectedGranularity={selectedImpactGranularity}
              onSelectGranularity={setSelectedImpactGranularity}
              comparisonNote={impactComparisonNote || undefined}
              claimSupported={claimBoundaryPresentation.hasCanonical
                ? isEffectivenessClaimAllowedFromBoundary(claimBoundaryPresentation.canonicalBoundary)
                : false}
              impactWaived={impactMeasurementWaived}
            />
          </Box>

          <Accordion sx={{ borderRadius: 3, border: '1px solid', borderColor: '#E5E7EB', boxShadow: 'none', '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMoreOutlined />} aria-controls="detailed-claims-content" id="detailed-claims-header">
              <Box>
                <Typography variant="h6">Detailed claims and confidence</Typography>
                <Typography variant="body2" color="text.secondary">
                  Full claim-boundary, confidence, and normalization detail retained for audit and compliance review.
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              <ClaimsConfidenceCard
                validatedClaim={validatedClaimText}
                keyInsight={keyInsightText}
                claimConfidence={claimConfidenceLabel}
                evidenceSufficiency={evidenceSufficiencyLabel}
                dataNormalization={dataNormalizationLabel}
                comparisonMethod={comparisonMethodLabel}
                exposureBasis={exposureBasisText}
                callout={summaryCalloutText}
              />
            </AccordionDetails>
          </Accordion>
        </AccordionDetails>
      </Accordion>
      ) : null}
        </>
      ) : (
        <ArtifactDetailSectionsSkeleton />
      )}
    </Box>
  );
};

export default SafetyReportArtifactDetail;
